<template>
    <div class="update-invoice-page">
      <!-- Loading -->
      <div v-if="pageLoading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="text-muted mt-2 mb-0">Loading invoice…</p>
      </div>

      <!-- Load error -->
      <b-alert v-else-if="loadError" variant="danger" show class="mb-3">
        {{ loadError }}
        <b-button size="sm" variant="outline-danger" class="ml-2" @click="$router.go(-1)">
          Go Back
        </b-button>
      </b-alert>

      <template v-else>
      <b-alert v-if="submitError" variant="danger" show dismissible class="mb-2">
        {{ submitError }}
      </b-alert>

      <b-row>
        <!-- Live invoice template preview (same as create flow) -->
        <b-col lg="8" class="mb-3">
          <div class="d-none d-lg-block live-preview-sticky">
            <div class="d-flex align-items-center preview-toolbar-row">
              <feather-icon icon="EyeIcon" size="14" class="text-primary mr-50" />
              <span class="small font-weight-bold text-uppercase text-muted">Live Invoice Preview</span>
              <b-badge variant="light-info" class="ml-1 preview-live-badge">Updates as you edit</b-badge>
            </div>
            <div class="live-preview-scroll">
              <div ref="previewPanelStage" class="a4-preview-stage">
                <div class="a4-preview-fit" :style="previewPanelFitStyle">
                  <div class="a4-preview-scaler" :style="previewPanelScalerStyle">
                    <generic-invoice-preview
                      ref="previewPanelRoot"
                      :template="previewTemplateForDisplay"
                      document-type="Tax Invoice"
                      :bill-from="previewBillFrom"
                      :bill-to="previewBillTo"
                      :invoice-meta="previewInvoiceMeta"
                      :status="previewInvoiceStatus"
                      :description="invoiceDescription"
                      :line-items="previewLineItemsForGeneric"
                      :totals="previewTotalsForGeneric"
                      :bank-details="previewBankDetailsForGeneric"
                      :columns="previewLineItemColumns"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Mobile: collapsible preview -->
          <div class="d-lg-none">
            <b-button
              block
              variant="outline-primary"
              size="sm"
              @click="showMobilePreview = !showMobilePreview"
            >
              <feather-icon :icon="showMobilePreview ? 'ChevronUpIcon' : 'EyeIcon'" size="14" class="mr-50" />
              {{ showMobilePreview ? 'Hide' : 'Show' }} Live Preview
            </b-button>
            <b-collapse v-model="showMobilePreview" class="mt-2">
              <div ref="previewMobileStage" class="a4-preview-stage">
                <div class="a4-preview-fit" :style="previewMobileFitStyle">
                  <div class="a4-preview-scaler" :style="previewMobileScalerStyle">
                    <generic-invoice-preview
                      ref="previewMobileRoot"
                      :template="previewTemplateForDisplay"
                      document-type="Tax Invoice"
                      :bill-from="previewBillFrom"
                      :bill-to="previewBillTo"
                      :invoice-meta="previewInvoiceMeta"
                      :status="previewInvoiceStatus"
                      :description="invoiceDescription"
                      :line-items="previewLineItemsForGeneric"
                      :totals="previewTotalsForGeneric"
                      :bank-details="previewBankDetailsForGeneric"
                      :columns="previewLineItemColumns"
                    />
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>
          <invoice-versions-section
            :invoice-id="invoiceId"
            :invoice="invoiceRecord"
            :invoice-status="invoiceStatus"
            :existing-file-path="existingInvoiceFilePath"
            :existing-file-data-url="blink"
            :pending-revised-file="revisedInvoiceFile"
            :revision-history="revisionHistory"
            :revision-history-loaded="revisionHistoryLoaded"
            :revision-history-source="revisionHistorySource"
            history-modal-id="modal-invoice-revision-history-update"
          />
        </b-col>

        <!-- Edit form -->
        <b-col lg="4">
      <b-card>
        <b-form @submit.prevent="onSubmit" class="wizard-step-compact update-invoice-form">
          <!-- Minimal context — always visible -->
          <div class="update-invoice-context mb-2 pb-2 border-bottom">
            <div class="d-flex align-items-center flex-wrap update-invoice-context-row">
              <span class="text-muted small text-uppercase mr-50">Invoice</span>
              <strong class="text-uppercase mr-1">{{ invoiceId || '—' }}</strong>
              <b-badge v-if="invoiceStatus" variant="light-primary" class="mr-50">{{ invoiceStatus }}</b-badge>
              <b-badge v-if="showContextR1Badge" :variant="contextR1BadgeVariant">{{ contextR1Badge }}</b-badge>
            </div>
          </div>

          <!-- Completed change summaries -->
          <div v-if="completedChangeTypes.length" class="update-session-changes mb-2">
            <small class="text-muted d-block mb-1 update-session-label">
              Changes in this session
            </small>
            <transition-group name="update-section-list" tag="div">
              <div
                v-for="id in completedChangeTypes"
                :key="'summary-' + id"
                class="wizard-summary-card update-section-summary rounded p-2 mb-2"
              >
                <div class="d-flex justify-content-between align-items-start gap-1">
                  <div class="flex-grow-1 min-w-0">
                    <div class="d-flex align-items-center flex-wrap">
                      <feather-icon :icon="changeTypeIcon(id)" size="13" class="text-primary mr-50 flex-shrink-0" />
                      <strong class="small">{{ changeTypeLabel(id) }}</strong>
                      <b-badge variant="light-success" class="ml-50 update-section-done-badge">Done</b-badge>
                    </div>
                    <div class="wizard-summary-lines text-muted small mt-50">{{ getSectionSummary(id) }}</div>
                  </div>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="wizard-icon-btn--sm flex-shrink-0"
                    :title="'Edit ' + changeTypeLabel(id)"
                    @click="expandSection(id)"
                  >
                    <feather-icon icon="Edit2Icon" size="12" />
                  </b-button>
                </div>
              </div>
            </transition-group>
          </div>

          <!-- Change type picker — one section at a time -->
          <div class="wizard-question" :class="{ 'mb-0': activeChangeType }">
            <wizard-section-header
              v-if="activeChangeType && !hideActiveSectionHeader"
              :icon="changeTypeIcon(activeChangeType)"
              :title="changeTypeLabel(activeChangeType)"
              :spaced="false"
              compact
            />
            <template v-else-if="!activeChangeType">
              <h6 class="wizard-question-heading">{{ changePickerHeading }}</h6>
              <span class="wizard-question-hint">{{ changePickerHint }}</span>
            </template>
            <div v-if="availableChangeTypeOptions.length && !activeChangeType" class="update-change-type-chips mt-2">
              <button
                v-for="opt in availableChangeTypeOptions"
                :key="opt.id"
                type="button"
                class="update-change-type-chip"
                :title="opt.hint"
                @click="selectChangeType(opt.id)"
              >
                <feather-icon :icon="opt.icon" size="13" class="chip-icon" />
                <span class="chip-label">{{ opt.label }}</span>
              </button>
            </div>
            <b-alert v-if="!hasSelectedChangeTypes && !activeChangeType" variant="light" show class="mt-2 mb-0 small py-2">
              <feather-icon icon="InfoIcon" size="13" class="mr-50" />
              Pick a change type to get started — you'll edit one section at a time.
            </b-alert>
          </div>

          <!-- Active section (only one expanded) -->
          <transition name="update-section-fade" mode="out-in">
          <div v-if="activeChangeType" :key="activeChangeType" class="update-section-active">

          <!-- STATUS -->
          <template v-if="showStatusSection && activeChangeType === 'status'">
            <b-form-group id="invoice-status" label="Invoice Status *" label-for="invoice-status" label-class="update-field-label mb-0" class="mb-0">
              <v-select
                  v-model="invoiceStatus"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  :options="invoiceStatusOptions"
                  placeholder="Select Invoice Status"
                  :reduce="(option) => option.title"
              />
              <small v-if="!invoiceStatus && error.invoiceStatus" class="text-danger">{{ error.invoiceStatus }}</small>
            </b-form-group>
            <b-row v-if="invoiceStatus === 'Cancelled'">
              <b-col col>
                <b-form-group label="Cancellation Reason *" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-select v-model="cancellationReason" :options="cancellationReasonOptions" size="sm" />
                  <small v-if="!cancellationReason" class="text-muted">Please select a reason for cancellation.</small>
                </b-form-group>
              </b-col>
              <b-col col v-if="cancellationReason === 'Invoice Filed and cannot be Amended'">
                <b-form-group label="Comments *" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-textarea v-model="cancellationComment" placeholder="Describe why it cannot be amended..." rows="2" size="sm" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="invoiceStatus === 'Unpaid'">
              <b-col col>
                <b-form-group id="invoice-payment-comment-group" label="Comment" label-for="invoice-payment-comment" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-textarea id="invoice-payment-comment" v-model="invoicePaymentComment" placeholder="Add Comment" rows="2" size="sm" />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="showR1Fields">
              <b-col col>
                <b-form-group id="r1-filed-group" label="Is R1 Filed?" label-for="r1-filed" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-select id="r1-filed" v-model="r1Filed" :options="r1FiledOptions" size="sm" />
                  <small v-if="!r1Filed && error.r1Filed" class="text-danger">{{ error.r1Filed }}</small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-alert v-if="invoiceStatus === 'Pending Approval'" variant="warning" show class="update-compact-alert mb-1">
              <feather-icon icon="ClockIcon" size="12" class="mr-50 flex-shrink-0" />
              Pending Approval — approvers can Approve &amp; Issue or Return to Maker.
            </b-alert>
            <b-alert v-if="invoiceStatus === 'Returned'" variant="danger" show class="update-compact-alert mb-1">
              <feather-icon icon="AlertTriangleIcon" size="12" class="mr-50 flex-shrink-0" />
              Returned by approver — review, update and re-submit.
            </b-alert>
          </template>

          <!-- BASIC DETAILS -->
          <template v-if="showBasicSection && activeChangeType === 'basic'">
            <b-row>
              <b-col col>
                <b-form-group id="invoice-id-group" label="Invoice ID *" label-for="invoice-id" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-input id="invoice-id"
                    class="text-uppercase"
                    v-model="invoiceId"
                    placeholder="SH100"
                    size="sm"
                    trim />
                  <small v-if="!invoiceId && error.invoiceId" class="text-danger">{{ error.invoiceId }}</small>
                </b-form-group>
              </b-col>
              <b-col col>
                <b-form-group id="invoice-date-group" label="Invoice Date *" label-for="invoice-date" label-class="update-field-label mb-0" class="mb-0">
                  <flat-pickr
                    class="form-control form-control-sm"
                    id="invoice-date"
                    v-model="invoiceDate"
                    placeholder="YYYY-MM-DD"
                    :config="{ enableTime: false}"
                  />
                  <small v-if="!invoiceDate && error.invoiceDate" class="text-danger">{{ error.invoiceDate }}</small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col col>
                <b-form-group id="invoice-category" label="Invoice Category *" label-for="invoice-category" label-class="update-field-label mb-0" class="mb-0">
                  <v-select
                      v-model="invoiceCategory"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      label="title"
                      taggable
                      push-tags
                      placeholder="Select Category"
                      :options="categories"
                      :reduce="(option) => option.title"
                  />
                  <small v-if="!invoiceCategory && error.invoiceCategory" class="text-danger">{{ error.invoiceCategory }}</small>
                </b-form-group>
              </b-col>
              <b-col col>
                <b-form-group id="invoice-location" label="Invoice Location *" label-for="invoice-location" label-class="update-field-label mb-0" class="mb-0">
                  <v-select
                      v-model="invoiceLocation"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      label="title"
                      :options="locations"
                      placeholder="Select Location"
                      :reduce="(option) => option.title"
                  />
                  <small v-if="!invoiceLocation && error.invoiceLocation" class="text-danger">{{ error.invoiceLocation }}</small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="invoiceStatus !== 'Cancelled' && invoiceCategory && invoiceCategory !== 'Recruitment' && invoiceCategory !== 'Contractual'">
              <b-col col>
                <b-form-group id="invoice-description" label="Invoice Description *" label-for="invoice-description" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-input
                    id="invoice-description"
                    v-model="invoiceDescription"
                    class="text-capitalize"
                    placeholder="Add Description"
                    size="sm"
                  />
                  <small v-if="!invoiceDescription && error.invoiceDescription" class="text-danger">{{ error.invoiceDescription }}</small>
                </b-form-group>
              </b-col>
              <b-col col>
                <b-form-group id="invoice-po-number" label="Invoice PO Number" label-for="invoice-po-number" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-input
                    id="invoice-po-number"
                    v-model="invoicePONumber"
                    class="text-capitalize"
                    placeholder="Add PO Number"
                    size="sm"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </template>

          <!-- CLIENT / BILLING -->
          <template v-if="showClientSection && activeChangeType === 'client'">
            <b-row class="update-client-row mb-0">
              <b-col cols="12" md="4">
                <b-form-group id="invoice-gst" label="Invoice GST Type *" label-for="invoice-gst" label-class="update-field-label mb-0" class="mb-0">
                  <v-select
                      v-model="invoiceGST"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      label="title"
                      :options="invoiceGSTOptions"
                      :reduce="(option) => option.title"
                      placeholder="Select GST Type"
                      @input="calculateTotalBaseAmount"
                  />
                  <small v-if="!invoiceGST && error.invoiceGST" class="text-danger">{{ error.invoiceGST }}</small>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group id="invoice-client" label="Invoice Client *" label-for="invoice-client" label-class="update-field-label mb-0" class="mb-0">
                  <v-select
                      v-model="invoiceClient"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      label="title"
                      placeholder="Select Client"
                      :options="clients"
                      :reduce="(option) => option.title"
                  />
                  <small v-if="!invoiceClient && error.invoiceClient" class="text-danger">{{ error.invoiceClient }}</small>
                  <span v-if="invoiceClient && invoiceCategory && (invoiceCategory === 'Recruitment' || invoiceCategory === 'Contractual')" class="text-primary" @click="getCandidateListModal"><u>Get Candidate List</u></span>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group id="invoice-client-gst" label="Client GST *" label-for="invoice-client-gst" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-input
                    id="invoice-client-gst"
                    v-model="invoiceClientGST"
                    class="text-capitalize"
                    placeholder="Add GST Number"
                    size="sm"
                    @change="validateGST" />
                  <small v-if="validateGSTError || error.invoiceClientGST" class="text-danger">{{ validateGSTError + error.invoiceClientGST }}</small>
                </b-form-group>
              </b-col>
            </b-row>
          </template>

          <!-- DOCUMENTS -->
          <template v-if="showDocumentSection && activeChangeType === 'document'">
            <b-form-group id="invoice-file" label="Invoice File *" label-for="invoice-file" label-class="update-field-label mb-0" class="mb-0">
              <b-form-file
                placeholder="Choose/drop PDF file..."
                drop-placeholder="Drop PDF file here..."
                name="invoiceFile"
                accept="application/pdf"
                size="sm"
                @change="openFile"
                v-model="invoiceFile"
                type="file"
              />
              <small v-if="!hasInvoiceFile && error.invoiceFile" class="text-danger">{{ error.invoiceFile }}</small>
              <span v-if="hasInvoiceFile" @click="previewFile()" class="text-primary cursor-pointer">Preview File</span>
            </b-form-group>
          </template>

          <!-- EMAIL & DATES -->
          <template v-if="showEmailDatesSection && activeChangeType === 'emailDates'">
            <b-row>
              <b-col col>
                <b-form-group id="invoice-email-date-group" label="Invoice Email Date *" label-for="invoice-email-date" label-class="update-field-label mb-0" class="mb-0">
                  <flat-pickr
                    class="form-control form-control-sm"
                    id="invoice-email-date"
                    v-model="invoiceEmailDate"
                    placeholder="YYYY-MM-DD"
                    :config="{ enableTime: false}"
                  />
                  <small v-if="!invoiceEmailDate && error.invoiceEmailDate" class="text-danger">{{ error.invoiceEmailDate }}</small>
                </b-form-group>
              </b-col>
              <b-col col>
                <b-form-group id="invoice-due-date-group" label="Invoice Due Date *" label-for="invoice-due-date" label-class="update-field-label mb-0" class="mb-0">
                  <flat-pickr
                    class="form-control form-control-sm"
                    id="invoice-due-date"
                    v-model="invoiceDueDate"
                    placeholder="YYYY-MM-DD"
                    :config="{ enableTime: false}"
                  />
                  <small v-if="!invoiceDueDate && error.invoiceDueDate" class="text-danger">{{ error.invoiceDueDate }}</small>
                </b-form-group>
              </b-col>
            </b-row>
            <b-alert v-if="invoiceEmailDate && invoiceDate && invoiceEmailDate !== invoiceDate" variant="warning" show class="update-compact-alert mb-1">
              <feather-icon icon="AlertTriangleIcon" size="12" class="mr-50 flex-shrink-0" />
              Email Date ({{ invoiceEmailDate }}) and Invoice Date ({{ invoiceDate }}) should match.
            </b-alert>
          </template>

          <!-- PAYMENT (standalone or inline with status when Paid/Part Paid) -->
          <template v-if="showPaymentSection && showPaymentFieldsActive">
            <b-row>
              <b-col col v-if="isPaidOrPartPaidStatus">
                <b-form-group id="invoice-paid-date-group" label="Invoice Paid Date *" label-for="invoice-paid-date" label-class="update-field-label mb-0" class="mb-0">
                  <flat-pickr
                    class="form-control form-control-sm"
                    id="invoice-paid-date"
                    v-model="invoicePaidDate"
                    placeholder="YYYY-MM-DD"
                    :config="{ enableTime: false}"
                  />
                  <small v-if="!invoicePaidDate && error.invoicePaidDate" class="text-danger">{{ error.invoicePaidDate }}</small>
                </b-form-group>
              </b-col>
              <b-col col v-if="isPaidOrPartPaidStatus">
                <b-form-group id="invoice-paid-amount-group" label="Paid Amount *" label-for="invoice-paid-amount" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-input
                    id="invoice-paid-amount"
                    v-model="invoicePaidAmount"
                    type="number"
                    placeholder="Enter paid amount"
                    min="0"
                    step="0.01"
                    size="sm"
                  />
                  <small v-if="!invoicePaidAmount && error.invoicePaidAmount" class="text-danger">{{ error.invoicePaidAmount }}</small>
                  <small v-else-if="totalAmountWithGSTTDS || totalAmountWithGST" class="text-muted d-block">
                    Invoice total: ₹{{ totalAmountWithGSTTDS || totalAmountWithGST }}
                  </small>
                </b-form-group>
              </b-col>
              <b-col col v-if="selectedChangeTypes.includes('payment') && !selectedChangeTypes.includes('emailDates')">
                <b-form-group id="invoice-due-date-payment-group" label="Invoice Due Date" label-for="invoice-due-date-payment" label-class="update-field-label mb-0" class="mb-0">
                  <flat-pickr
                    class="form-control form-control-sm"
                    id="invoice-due-date-payment"
                    v-model="invoiceDueDate"
                    placeholder="YYYY-MM-DD"
                    :config="{ enableTime: false}"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="isPartPaidStatus">
              <b-col col>
                <b-form-group label="Amount check" label-class="update-field-label mb-0" class="mb-0">
                  <small v-if="totalAmountWithGST && invoicePaidAmount" :class="parseAmount(invoicePaidAmount) > parseAmount(totalAmountWithGST) ? 'text-danger' : 'text-muted'">
                    Expected (with GST): ₹{{ totalAmountWithGST }}
                    <span v-if="parseAmount(invoicePaidAmount) > parseAmount(totalAmountWithGST)"> — Paid amount exceeds total!</span>
                  </small>
                </b-form-group>
              </b-col>
              <b-col col>
                <b-form-group label="Is GST on Hold?" label-class="update-field-label mb-0" class="mb-0 update-radio-field">
                  <b-form-radio-group
                    v-model="isGSTHold"
                    size="sm"
                    name="gst-on-hold"
                    class="update-radio-group-buttons mb-0"
                    button-variant="outline-primary"
                    buttons
                    :options="[{ text: 'No', value: false }, { text: 'Yes', value: true }]"
                  />
                </b-form-group>
              </b-col>
              <b-col col v-if="isGSTHold">
                <b-form-group label="Reason for GST Hold" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-textarea v-model="gstHoldReason" placeholder="Why is GST on hold?" rows="2" size="sm" />
                </b-form-group>
              </b-col>
            </b-row>
          </template>

          <!-- REVISION WORKFLOW -->
          <template v-if="showRevisionSection && showRevisionFieldsActive">
            <b-row v-if="!r1Filed && invoiceStatus === 'Revised'">
              <b-col col>
                <b-form-group id="r1-filed-revision-group" label="Is R1 Filed?" label-for="r1-filed-revision" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-select id="r1-filed-revision" v-model="r1Filed" :options="r1FiledOptions" size="sm" />
                  <small v-if="!r1Filed && error.r1Filed" class="text-danger">{{ error.r1Filed }}</small>
                </b-form-group>
              </b-col>
            </b-row>
            <template v-if="invoiceStatus === 'Revised' && r1Filed">
            <div class="update-revision-panel mb-1">

              <template v-if="r1Filed === 'Yes'">
                <b-form-group label="Revision Reason" label-class="update-field-label mb-0" class="mb-0 update-radio-field">
                  <b-form-radio-group
                    v-model="revisionType"
                    size="sm"
                    name="revision-type"
                    class="update-radio-group-buttons mb-0"
                    button-variant="outline-primary"
                    buttons
                    :options="[{ text: 'Basic Info Change', value: 'basic' }, { text: 'Amount Change', value: 'amount' }]"
                  />
                </b-form-group>

                <template v-if="revisionType === 'basic'">
                  <b-form-group label="Upload Revised Invoice" label-class="update-field-label mb-0" class="mb-0">
                    <b-form-file v-model="revisedInvoiceFile" accept=".pdf,.jpg,.png" placeholder="Upload revised invoice" size="sm" />
                  </b-form-group>
                  <b-form-group label="Amend Invoice?" label-class="update-field-label mb-0" class="mb-0 update-radio-field">
                    <b-form-radio-group
                      v-model="amendInvoice"
                      size="sm"
                      name="amend-invoice-r1-yes-basic"
                      class="update-radio-group-buttons mb-0"
                      button-variant="outline-primary"
                      buttons
                      :options="[{ text: 'Yes — R1 required', value: 'yes' }, { text: 'No — Invoice only', value: 'no' }]"
                    />
                  </b-form-group>
                  <b-form-group v-if="amendInvoice === 'yes'" label="Upload R1 Document" label-class="update-field-label mb-0" class="mb-0">
                    <b-form-file v-model="r1UploadFile" accept=".pdf,.json,.xlsx" placeholder="Upload R1 document" size="sm" />
                  </b-form-group>
                  <b-alert variant="info" show class="update-compact-alert mb-1">
                    <feather-icon icon="InfoIcon" size="12" class="mr-50 flex-shrink-0" />
                    Invoice # stays the same; each Revised save appends history.
                  </b-alert>
                </template>

                <template v-if="revisionType === 'amount'">
                  <b-form-group label="Upload Revised Invoice" label-class="update-field-label mb-0" class="mb-0">
                    <b-form-file v-model="revisedInvoiceFile" accept=".pdf,.jpg,.png" placeholder="Upload revised invoice" size="sm" />
                  </b-form-group>
                  <b-form-group label="Base Amount Change" label-class="update-field-label mb-0" class="mb-0 update-radio-field">
                    <b-form-radio-group
                      v-model="amountChangeType"
                      size="sm"
                      name="amount-change-type"
                      class="update-radio-group-buttons mb-0"
                      button-variant="outline-primary"
                      buttons
                      :options="[{ text: 'Increased — Debit Note', value: 'increase' }, { text: 'Decreased — Credit Note', value: 'decrease' }]"
                    />
                  </b-form-group>
                  <template v-if="amountChangeType">
                    <b-form-group :label="amountChangeType === 'increase' ? 'Upload Debit Note' : 'Upload Credit Note'" label-class="update-field-label mb-0" class="mb-0">
                      <b-form-file v-model="noteFile" accept=".pdf,.jpg,.png" :placeholder="amountChangeType === 'increase' ? 'Upload Debit Note' : 'Upload Credit Note'" size="sm" />
                    </b-form-group>
                    <b-alert v-if="noteDateError" variant="danger" show class="update-compact-alert mb-1">
                      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-50 flex-shrink-0" />
                      {{ noteDateError }}
                    </b-alert>
                    <b-form-group :label="(amountChangeType === 'increase' ? 'Debit' : 'Credit') + ' Note Date'" label-class="update-field-label mb-0" class="mb-0">
                      <flat-pickr
                        v-model="noteDate"
                        class="form-control form-control-sm"
                        :config="{ enableTime: false, dateFormat: 'Y-m-d', maxDate: invoiceDate || 'today' }"
                        placeholder="YYYY-MM-DD"
                      />
                      <small class="text-muted">Must not be later than Invoice Date ({{ invoiceDate }}).</small>
                    </b-form-group>
                    <b-form-group label="Sent to Client?" label-class="update-field-label mb-0" class="mb-0 update-radio-field">
                      <b-form-radio-group
                        v-model="noteSentToClient"
                        size="sm"
                        name="note-sent-to-client"
                        class="update-radio-group-buttons mb-0"
                        button-variant="outline-primary"
                        buttons
                        :options="[{ text: 'Yes', value: true }, { text: 'No', value: false }]"
                      />
                    </b-form-group>
                    <b-form-group label="E-Invoice Done?" label-class="update-field-label mb-0" class="mb-0 update-radio-field">
                      <b-form-radio-group
                        v-model="noteEInvoiceDone"
                        size="sm"
                        name="note-e-invoice-done"
                        class="update-radio-group-buttons mb-0"
                        button-variant="outline-primary"
                        buttons
                        :options="[{ text: 'Yes', value: true }, { text: 'No', value: false }]"
                      />
                    </b-form-group>
                  </template>
                </template>
              </template>

              <template v-if="r1Filed === 'No'">
                <b-alert variant="info" show class="update-compact-alert mb-1">
                  <feather-icon icon="InfoIcon" size="12" class="mr-50 flex-shrink-0" />
                  Filing month not crossed — ensure invoice is filed this month.
                </b-alert>
                <b-form-group label="Reason for Revision" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-textarea v-model="revisionReason" placeholder="Why is this invoice being revised?" rows="2" size="sm" />
                </b-form-group>
                <b-form-group label="Upload Invoice" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-file v-model="revisedInvoiceFile" accept=".pdf,.jpg,.png" placeholder="Upload invoice document" size="sm" />
                </b-form-group>
                <b-form-group label="Amend Invoice?" label-class="update-field-label mb-0" class="mb-0 update-radio-field">
                  <b-form-radio-group
                    v-model="amendInvoice"
                    size="sm"
                    name="amend-invoice-r1-no"
                    class="update-radio-group-buttons mb-0"
                    button-variant="outline-primary"
                    buttons
                    :options="[{ text: 'Yes', value: 'yes' }, { text: 'No', value: 'no' }]"
                  />
                </b-form-group>
                <b-alert variant="warning" show class="update-compact-alert mb-0">
                  <feather-icon icon="CalendarIcon" size="12" class="mr-50 flex-shrink-0" />
                  Ensure invoice email is filed in the coming month.
                </b-alert>
              </template>
            </div>
            </template>
            <b-alert v-else-if="!r1Filed" variant="light" show class="update-compact-alert mb-0">
              <feather-icon icon="InfoIcon" size="12" class="mr-50 flex-shrink-0" />
              Select whether R1 has been filed to continue.
            </b-alert>
          </template>

          <!-- AMOUNTS -->
          <template v-if="showAmountsSection && invoiceStatus !== 'Cancelled' && activeChangeType === 'amounts'">

            <small class="wizard-question-hint d-block mb-1">
              Edit line items or base amount below — preview updates live.
            </small>

            <div v-if="showAmountsLineItemsEditor" class="mb-0 update-line-items-editor">
              <div class="d-flex justify-content-between align-items-center mb-0">
                <span class="update-section-subheading">Line items</span>
                <b-button size="sm" variant="outline-primary" @click="addLineItem">
                  <feather-icon icon="PlusIcon" size="12" class="mr-50" />
                  Add line item
                </b-button>
              </div>
              <b-table
                striped
                hover
                small
                responsive
                :items="lineItems"
                :fields="amountsLineItemFields"
                class="mb-0 update-line-items-table"
              >
                <template #cell(itemName)="data">
                  <v-select
                    :value="data.item.itemName || data.item.title || null"
                    :options="lineItemTitleOptions"
                    :create-option="createLineItemTitleOption"
                    taggable
                    push-tags
                    clearable
                    placeholder="Select a title (optional)"
                    class="update-line-item-title-select"
                    @input="onLineItemTitleChange(data.index, $event)"
                  >
                    <template #no-options="{ search }">
                      <span v-if="search">Use "{{ search }}"</span>
                      <span v-else>Optional — pick a title or type a custom one.</span>
                    </template>
                  </v-select>
                </template>
                <template #cell(quantity)="data">
                  <b-form-input
                    v-model.number="data.item.quantity"
                    type="number"
                    step="0.01"
                    min="0"
                    size="sm"
                    class="text-right"
                    @input="onLineItemFieldChange(data.index)"
                  />
                </template>
                <template #cell(unitPrice)="data">
                  <b-form-input
                    v-model.number="data.item.unitPrice"
                    type="number"
                    step="0.01"
                    min="0"
                    size="sm"
                    class="text-right"
                    @input="onLineItemFieldChange(data.index)"
                  />
                </template>
                <template #cell(amount)="data">
                  <b-form-input
                    v-model.number="data.item.amount"
                    type="number"
                    step="0.01"
                    min="0"
                    size="sm"
                    class="text-right"
                    @input="onLineItemAmountChange(data.index)"
                  />
                </template>
                <template #cell(actions)="data">
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    class="p-50"
                    :disabled="lineItems.length <= 1"
                    @click="removeLineItem(data.index)"
                  >
                    <feather-icon icon="Trash2Icon" size="12" />
                  </b-button>
                </template>
              </b-table>
            </div>

            <b-row v-if="isCandidateCategory && showInvoiceCandidateList">
              <b-col md="12">
                <b-table striped hover small responsive :items="invoiceCandidateList" :fields="showInvoiceCandidateFields" class="update-line-items-table mb-0">
                  <template #cell(candidate_name)="data">
                    <div>{{ data.value }}</div>
                  </template>
                  <template #cell(position_name)="data">
                    <div>{{ data.value }}</div>
                  </template>
                  <template #cell(base_amount)="data">
                    <b-form-input
                      v-model="data.item.base_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      size="sm"
                      @input="onCandidateAmountChange"
                    />
                  </template>
                </b-table>
              </b-col>
            </b-row>
            <b-row v-if="isCandidateCategory && noInvoiceCandidateList">
              <b-col md="12" class="mb-2 text-muted small">
                {{ noInvoiceCandidateListError }}
              </b-col>
            </b-row>

            <b-row v-if="showBaseAmountInput">
              <b-col cols="12" md="6">
                <b-form-group
                  id="invoice-amount"
                  label="Base amount *"
                  label-for="invoice-amount"
                  label-class="update-field-label mb-0"
                  class="mb-0"
                >
                  <b-form-input
                    id="invoice-amount"
                    v-model="invoiceBaseAmount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g. 100000"
                    size="sm"
                    @input="onBaseAmountInput"
                  />
                  <small v-if="error.invoiceBaseAmount" class="text-danger">{{ error.invoiceBaseAmount }}</small>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" class="d-flex align-items-end pb-1">
                <b-button size="sm" variant="outline-secondary" @click="switchToLineItems">
                  <feather-icon icon="PlusIcon" size="12" class="mr-50" />
                  Switch to line items
                </b-button>
              </b-col>
            </b-row>

            <b-row v-if="showAmountsTotals && !showClientSection" class="mb-0 update-gst-row align-items-end">
              <b-col cols="6">
                <b-form-group label="GST Type" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-select
                    v-model="invoiceGST"
                    :options="invoiceGSTSelectOptions"
                    size="sm"
                    @change="calculateTotalBaseAmount"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="GST rate (%)" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-input
                    v-model.number="gstRatePercent"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    size="sm"
                    @input="calculateTotalBaseAmount"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row v-if="showAmountsTotals" class="mb-0 align-items-center update-tds-row">
              <b-col :cols="showClientSection ? 6 : 12">
                <div class="update-tds-inline d-flex align-items-center flex-nowrap">
                  <span class="update-field-label mb-0 mr-1 flex-shrink-0">TDS applicable?</span>
                  <b-form-checkbox v-model="tdsApplicable" switch size="sm" class="mb-0 update-tds-switch" @change="calculateTotalBaseAmount">
                    Deduct TDS from base ({{ tdsPercentage }}%)
                  </b-form-checkbox>
                </div>
              </b-col>
              <b-col v-if="showClientSection" cols="6">
                <b-form-group label="GST rate (%)" label-class="update-field-label mb-0" class="mb-0">
                  <b-form-input
                    v-model.number="gstRatePercent"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    size="sm"
                    @input="calculateTotalBaseAmount"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row v-if="showAmountsTotals" class="amounts-totals-row mb-0">
              <b-col cols="6" md="3" class="update-total-item">
                <span class="update-total-label">Total Base Amount</span>
                <span class="update-total-value">{{ formatAmountDisplay(totalBaseAmount) }}</span>
              </b-col>
              <b-col cols="6" md="3" class="update-total-item">
                <template v-if="invoiceGST === 'SEZ'">
                  <span class="update-total-label">Total GST</span>
                  <span class="update-total-value">{{ formatAmountDisplay(totalGST) }}</span>
                </template>
                <template v-else-if="invoiceGST === 'IGST'">
                  <span class="update-total-label">Total IGST</span>
                  <span class="update-total-value">{{ formatAmountDisplay(totalGST) }}</span>
                </template>
                <template v-else-if="invoiceGST === 'SGST-CGST'">
                  <span class="update-total-label">Total CGST</span>
                  <span class="update-total-value">{{ formatAmountDisplay(splitGstAmounts.cgst) }}</span>
                  <span class="update-total-label mt-25">Total SGST</span>
                  <span class="update-total-value">{{ formatAmountDisplay(splitGstAmounts.sgst) }}</span>
                </template>
              </b-col>
              <b-col cols="6" md="3" class="update-total-item">
                <span class="update-total-label">Total Amount with GST</span>
                <span class="update-total-value">{{ formatAmountDisplay(totalAmountWithGST) }}</span>
              </b-col>
              <b-col cols="6" md="3" class="update-total-item">
                <span class="update-total-label">Total Amount (TDS + GST)</span>
                <span class="update-total-value">{{ formatAmountDisplay(totalAmountWithGSTTDS) }}</span>
                <span v-if="tdsApplicable && getTdsAmount() > 0" class="update-total-hint">
                  TDS deducted: {{ formatAmountDisplay(getTdsAmount()) }}
                </span>
              </b-col>
            </b-row>
          </template>

          <!-- OTHER / COMMENT -->
          <template v-if="showOtherSection && activeChangeType === 'other'">
            <b-form-group label="Comment" label-class="update-field-label mb-0" class="mb-0">
              <b-form-textarea v-model="invoiceComment" placeholder="Add Comment" rows="2" size="sm" />
            </b-form-group>
          </template>

          <div class="update-section-actions">
            <b-button variant="primary" size="sm" class="wizard-nav-btn" @click="completeSection(activeChangeType)">
              <feather-icon icon="CheckIcon" size="14" class="mr-50" />
              Done
            </b-button>
            <b-button variant="outline-secondary" size="sm" class="wizard-nav-btn ml-50" @click="cancelActiveSection">
              Cancel
            </b-button>
          </div>

          </div>
          </transition><!-- /active section -->

          <b-alert
            v-if="hasSelectedChangeTypes && !activeChangeType && allSectionsCompleted"
            variant="light"
            show
            class="mt-2 mb-0 small py-2"
          >
            <feather-icon icon="CheckCircleIcon" size="13" class="mr-50 text-success" />
            All selected changes are complete. Review summaries above, then save.
          </b-alert>

          <b-row v-if="hasSelectedChangeTypes">
              <b-col cols="12" class="mt-3 wizard-nav-actions">
                <b-button type="submit" variant="primary" size="sm" class="wizard-nav-btn">
                  <feather-icon icon="SaveIcon" size="14" class="mr-1" />
                  Save Changes
                </b-button>
                <template v-if="invoiceStatus === 'Pending Approval' && $can('approve','invoice')">
                  <b-button variant="success" size="sm" class="wizard-nav-btn" @click="approveInvoice">
                    <feather-icon icon="CheckCircleIcon" size="14" class="mr-1" />
                    Approve &amp; Issue
                  </b-button>
                  <b-button variant="warning" size="sm" class="wizard-nav-btn" @click="returnInvoice">
                    <feather-icon icon="CornerUpLeftIcon" size="14" class="mr-1" />
                    Return to Maker
                  </b-button>
                </template>
                <b-button variant="outline-secondary" size="sm" class="wizard-nav-btn ml-auto" @click="$router.go(-1)">
                  Cancel
                </b-button>
              </b-col>
          </b-row>
        </b-form>
        <invoice-timeline-card
          class="mt-2"
          :invoice-timeline="invoiceTimeline"
          :loading="invoiceTimelineLoading"
          :invoice-status="invoiceStatus || (invoiceRecord && invoiceRecord.status) || ''"
          :timeline-meta="invoiceTimelineMeta"
        />
        <b-card v-if="blink" class="mt-2" no-body>
          <b-card-header header-tag="header" class="p-1" role="tab">
            <b-button v-b-toggle.uploaded-pdf-panel block variant="link" class="text-left p-1">
              <feather-icon icon="FileTextIcon" size="14" class="mr-50" />
              Uploaded PDF
            </b-button>
          </b-card-header>
          <b-collapse id="uploaded-pdf-panel" visible>
            <b-card-body class="p-1">
              <iframe :src="blink" frameborder="0" height="600px" width="100%" title="Uploaded invoice PDF" />
            </b-card-body>
          </b-collapse>
        </b-card>
        <b-modal v-model="showModal" :fields="modalCandidateListFields" title="Select Candidates" @ok="getCandidateSelectedList" size="xl">
            
            <b-table 
              ref="selectableTable"
              striped 
              hover 
              :items="candidateList" 
              :fields="modalCandidateListFields"
              :filter="filter"
              select-mode="multi"
              selectable
              @row-selected="onRowSelected" >
              <template #cell(candidate_id)="{ rowSelected }">
                <template v-if="rowSelected">
                  <span aria-hidden="true">&check;</span>
                  <span class="sr-only">Selected </span>
                </template>
                <template v-else>
                  <span aria-hidden="true">&nbsp;</span>
                  <span class="sr-only">Not selected</span>
                </template>
              </template>
              <template #cell(candidate_name)="data">
                <div vertical-align="center">
                  <b-link
                    :to="{ name: 'apps-candidates-view', params: { id: data.item.cid, jobId: data.item.jid } }"
                    class="font-weight-bold d-block text-nowrap"
                  >
                    {{ data.item.candidate_name }} <br>
                    {{ data.item.candidate_id }}
                  </b-link>

                </div>
              </template>
              <template #cell(position_name)="data">
                <div class="text-nowrap">
                  <span class="align-text-top text-capitalize" >{{ data.item.position_name }}</span>
                </div>
              </template>
              <template #cell(client)="data">
                <div class="text-nowrap">
                  <span class="align-text-top text-capitalize" >{{ data.item.client }}</span>
                </div>
              </template>
              <template #cell(joining_date)="data">
                <div class="text-nowrap">
                  <!--<span class="align-text-top text-capitalize" v-if="data.item.jobs[0]">{{ JSON.parse(data.item.jobs[0].candidate_jobs.addedBy)[0].title }}</span>-->
                  <!--<span class="align-text-top text-capitalize" v-if="data.item.jobs[0]">{{ data.item.jobs[0].candidate_jobs.source }}</span>-->
                  <span class="align-text-top text-capitalize" >{{ data.item.joining_date }}</span>
                </div>
              </template>
            </b-table>
        </b-modal>
        <b-modal
          id="modal-xl-invoice-preview"
          ref="modal-xl-invoice-preview"
          ok-only
          ok-title="Close"
          centered
          size="xl"
          title="Extra Large Modal"
        >
          <div>            
              <iframe :src="blink" frameborder="0" height="1200px" width="100%"></iframe>
          </div>
        </b-modal>
        
        
      </b-card>
        </b-col>
      </b-row>
      <invoice-revision-history-modal
        modal-id="modal-invoice-revision-history-update"
        :invoice="invoiceRecord"
        :revision-history="revisionHistoryLoaded ? revisionHistory : null"
      />
      </template>
    </div>
  </template>
  
  <script>
  

  import {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BLink,
    BTable,
    BButton,
    BFormFile,
    BFormInvalidFeedback,
    BModal, VBModal,
    BFormSelect,
    BFormTextarea,
    BAlert,
    BFormCheckbox,
    BFormRadioGroup,
    BSpinner,
    BBadge,
    BCollapse,
    VBToggle,
} from 'bootstrap-vue'
  import vSelect from 'vue-select'
  import flatPickr from 'vue-flatpickr-component'
  import { mapState, mapGetters, mapActions } from 'vuex'
  import clientService from '@/services/client-service'
  import candidateService from '@/services/candidate-service'
  import { ValidationProvider, ValidationObserver } from 'vee-validate'
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
  import invoiceService, { formatInvoiceApiError } from '@/services/invoice-service'
  import enhancedInvoiceRevisionService from '@/services/enhanced-invoice-revision-service'
  import invoiceTimelineService from '@/services/invoice-timeline-service'
  import InvoiceTimelineCard from './components/InvoiceTimelineCard.vue'
  import InvoiceVersionsSection from './components/InvoiceVersionsSection.vue'
  import InvoiceRevisionHistoryModal from './components/modals/InvoiceRevisionHistoryModal.vue'
  import GenericInvoicePreview from './components/GenericInvoicePreview.vue'
  import WizardSectionHeader from './components/wizard/WizardSectionHeader.vue'
  import Ripple from 'vue-ripple-directive'
  import JobService from "@/services/job-service";
  import locationService from '@/services/location-service'
  import { getInvoiceFileSizeError, INVOICE_FILE_TOO_LARGE_MESSAGE } from '@/utils/invoice-filename'
  import {
    toVSelectStatusOptions,
    REVISED_STATUS,
    isRevisedStatusValue,
    extractUpdatedInvoiceStatus,
  } from '@/utils/invoice-status-options'
  import { DEFAULT_TEMPLATE, buildLineItemTitleOptions } from '@/utils/invoice-calculation-engine'
  import { resolveInvoiceApiKey } from '@/utils/invoice-api-key'

  export default {
    data() {
      return {
        invoiceId: '',
        invoiceDate: '',
        invoicePaidDate: '',
        clients: [],
        invoiceClient: null,
        invoiceClientGST: null,
        showModal: false,
        showInvoiceCandidateList: false,
        noInvoiceCandidateList: false,
        noInvoiceCandidateListError: '',
        candidateList: [],
        categories: [
          { title: 'Recruitment'},
          { title: 'Training'},
          { title: 'Anti Fraud'},
          { title: 'Contractual'},
          { title: 'Payroll'},
          { title: 'BGV'},
          { title: 'Miscellanous'},
        ],
        showInvoiceCandidateFields: [
          { key: 'candidate_name', label: 'Candidate name' },
          { key: 'position_name', label: 'Position Name' },
          { key: 'base_amount', label: 'Base Amount' },
        ],
        selectedCandidates: [],
        selectedCandidateFields: [
        { key: 'name', label: 'Name' },
            { key: 'amountGenerated', label: 'Amount Generated' },
            { key: 'gst', label: 'GST' },
            { key: 'exactRevenue', label: 'Exact Revenue' },
        ],
        invoiceStatus: '',
        invoiceStatusOptions: toVSelectStatusOptions(),
        invoiceGSTOptions: [
            { title: 'SEZ', label: 'SEZ' },
            { title: 'IGST', label: 'IGST'},
            { title: 'SGST-CGST', label: 'SGST-CGST'},
        ],
        invoiceGST: '',
        invoiceLocation: '',
        locations: [{"title":"Mumbai"},{"title":"Delhi"},{"title":"Bangalore"},{"title":"Hyderabad"},{"title":"Pune"},{"title":"Ahmedabad"},{"title":"Chennai"},{"title":"Kolkata"},{"title":"Surat"},{"title":"Jaipur"},{"title":"Lucknow"},{"title":"Kanpur"},{"title":"Nagpur"},{"title":"Indore"},{"title":"Thane"},{"title":"Bhopal"},{"title":"Visakhapatnam"},{"title":"Pimpri and Chinchwad"},{"title":"Patna"},{"title":"Vadodara"},{"title":"Ghaziabad"},{"title":"Ludhiana"},{"title":"Agra"},{"title":"Nashik"},{"title":"Faridabad"},{"title":"Meerut"},{"title":"Rajkot"},{"title":"Kalyan and Dombivali"},{"title":"Vasai Virar"},{"title":"Varanasi"},{"title":"Srinagar"},{"title":"Aurangabad"},{"title":"Dhanbad"},{"title":"Amritsar"},{"title":"Navi Mumbai"},{"title":"Allahabad"},{"title":"Haora"},{"title":"Ranchi"},{"title":"Gwalior"},{"title":"Jabalpur"},{"title":"Coimbatore"},{"title":"Vijayawada"},{"title":"Jodhpur"},{"title":"Madurai"},{"title":"Raipur"},{"title":"Kota"},{"title":"Chandigarh"},{"title":"Guwahati"},{"title":"Solapur"},{"title":"Hubli and Dharwad"},{"title":"Bareilly"},{"title":"Mysore"},{"title":"Moradabad"},{"title":"Gurgaon"},{"title":"Aligarh"},{"title":"Jalandhar"},{"title":"Tiruchirappalli"},{"title":"Bhubaneswar"},{"title":"Salem"},{"title":"Mira and Bhayander"},{"title":"Thiruvananthapuram"},{"title":"Bhiwandi"},{"title":"Saharanpur"},{"title":"Gorakhpur"},{"title":"Guntur"},{"title":"Amravati"},{"title":"Bikaner"},{"title":"Noida"},{"title":"Jamshedpur"},{"title":"Bhilai Nagar"},{"title":"Warangal"},{"title":"Cuttack"},{"title":"Firozabad"},{"title":"Kochi"},{"title":"Bhavnagar"},{"title":"Dehradun"},{"title":"Durgapur"},{"title":"Asansol"},{"title":"Nanded Waghala"},{"title":"Kolapur"},{"title":"Ajmer"},{"title":"Gulbarga"},{"title":"Loni"},{"title":"Ujjain"},{"title":"Siliguri"},{"title":"Ulhasnagar"},{"title":"Jhansi"},{"title":"Sangli Miraj Kupwad"},{"title":"Jammu"},{"title":"Nellore"},{"title":"Mangalore"},{"title":"Belgaum"},{"title":"Jamnagar"},{"title":"Tirunelveli"},{"title":"Malegaon"},{"title":"Gaya"},{"title":"Ambattur"},{"title":"Jalgaon"},{"title":"Udaipur"},{"title":"Maheshtala"},{"title":"Tiruppur"},{"title":"Davanagere"},{"title":"Kozhikode"},{"title":"Kurnool"},{"title":"Akola"},{"title":"Rajpur Sonarpur"},{"title":"Bokaro Steel"},{"title":"Bellary"},{"title":"Patiala"},{"title":"South Dum Dum"},{"title":"Rajarhat Gopalpur"},{"title":"Bhagalpur"},{"title":"Agartala"},{"title":"Muzaffarnagar"},{"title":"Bhatpara"},{"title":"Latur"},{"title":"Panihati"},{"title":"Dhule"},{"title":"Rohtak"},{"title":"Korba"},{"title":"Bhilwara"},{"title":"Brahmapur Town"},{"title":"Muzaffarpur"},{"title":"Ahmadnagar"},{"title":"Mathura"},{"title":"Kollam"},{"title":"Avadi"},{"title":"Kadapa"},{"title":"Rajahmundry"},{"title":"Bilaspur"},{"title":"Kamarhati"},{"title":"Shahjahanpur"},{"title":"Bijapur"},{"title":"Rampur"},{"title":"Shimoga"},{"title":"Chandrapur"},{"title":"Junagadh"},{"title":"Thrissur"},{"title":"Alwar"},{"title":"Barddhaman"},{"title":"Kulti"},{"title":"Kakinada"},{"title":"Nizamabad"},{"title":"Parbhani"},{"title":"Tumkur"},{"title":"Hisar"},{"title":"Ozhukarai"},{"title":"Biharsharif"},{"title":"Darbhanga"},{"title":"Panipat"},{"title":"Aizawl"},{"title":"Bally"},{"title":"Dewas"},{"title":"Tirupati"},{"title":"Ichalkaranji"},{"title":"Karnal"},{"title":"Bathinda"},{"title":"Jalna"},{"title":"Kirari Suleman Nagar"},{"title":"Purnia"},{"title":"Satna"},{"title":"Maunath Bhanjan"},{"title":"Barasat"},{"title":"Sonipat"},{"title":"Farrukhabad and Fatehgarh"},{"title":"Sagar"},{"title":"Raurkela"},{"title":"Durg"},{"title":"Imphal"},{"title":"Ratlam"},{"title":"Hapur"},{"title":"Arrah"},{"title":"Karimnagar"},{"title":"Anantapur"},{"title":"NDMC"},{"title":"Etawah"},{"title":"Ambernath"},{"title":"Bharatpur"},{"title":"Begusarai"},{"title":"Tiruvottiyur"},{"title":"North Dum Dum"},{"title":"Gandhidham"},{"title":"Baranagar"},{"title":"Puducherry"},{"title":"Thoothukkudi"},{"title":"Sikar"},{"title":"Rewa"},{"title":"Mirzapur and Vindhyachal"},{"title":"Raichur"},{"title":"Pali"},{"title":"Ramagundam"},{"title":"Hardwar"},{"title":"Vizianagaram"},{"title":"Katihar"},{"title":"Nagercoil"},{"title":"Ganganagar"},{"title":"Karawal Nagar"},{"title":"Mango"},{"title":"Thanjavur"},{"title":"Bulandshahr"},{"title":"Uluberia"},{"title":"Murwara"},{"title":"Sambhal"},{"title":"Singrauli"},{"title":"Nadiad"},{"title":"Secunderabad"},{"title":"Naihati"},{"title":"Yamunanagar"},{"title":"Bidhan Nagar"},{"title":"Pallavaram"},{"title":"Bidar"},{"title":"Munger"},{"title":"Panchkula"},{"title":"Burhanpur"},{"title":"Raurkela Industrial Township"},{"title":"Kharagpur"},{"title":"Dindigul"},{"title":"Hospet"},{"title":"Gandhinagar"},{"title":"Nangloi Jat"},{"title":"English Bazar"},{"title":"Ongole"},{"title":"Eluru"},{"title":"Deoghar"},{"title":"Chapra"},{"title":"Haldia"},{"title":"Khandwa"},{"title":"Puri Town"},{"title":"Nandyal"},{"title":"Morena"},{"title":"Amroha"},{"title":"Anand"},{"title":"Bhind"},{"title":"Bhalswa Jahangir Pur"},{"title":"Madhyamgram"},{"title":"Bhiwani"},{"title":"Navi Mumbai Panvel Raigad"},{"title":"Baharampur"},{"title":"Ambala"},{"title":"Morvi"},{"title":"Fatehpur"},{"title":"Rae Bareli"},{"title":"Khora"},{"title":"Bhusawal"},{"title":"Orai"},{"title":"Bahraich"},{"title":"Vellore"},{"title":"Mahesana"},{"title":"Khammam"},{"title":"Sambalpur"},{"title":"Raiganj"},{"title":"Sirsa"},{"title":"Dinapur Nizamat"},{"title":"Serampore"},{"title":"Sultan Pur Majra"},{"title":"Guna"},{"title":"Jaunpur"},{"title":"Panvel"},{"title":"Shivpuri"},{"title":"Surendranagar Dudhrej"},{"title":"Unnao"},{"title":"Hugli and Chinsurah"},{"title":"Sitapur"},{"title":"Hastsal"},{"title":"Tambaram"},{"title":"Adityapur"},{"title":"Badalapur"},{"title":"Alappuzha"},{"title":"Cuddalore"},{"title":"Silchar"},{"title":"Gadag and Betigeri"},{"title":"Bahadurgarh"},{"title":"Machilipatnam"},{"title":"Shimla"},{"title":"Medinipur"},{"title":"Deoli"},{"title":"Bharuch"},{"title":"Hoshiarpur"},{"title":"Jind"},{"title":"Chandannagar"},{"title":"Adoni"},{"title":"Tonk"},{"title":"Faizabad"},{"title":"Tenali"},{"title":"Alandur"},{"title":"Kancheepuram"},{"title":"Vapi"},{"title":"Rajnandgaon"},{"title":"Proddatur"},{"title":"Navsari"},{"title":"Budaun"},{"title":"Uttarpara Kotrung"},{"title":"Mahbubnagar"},{"title":"Erode"},{"title":"Batala"},
      {"title":"Saharsa"},{"title":"Haldwani and Kathgodam"},{"title":"Vidisha"},{"title":"Thanesar"},{"title":"Kishangarh"},{"title":"Dallo Pura"},{"title":"Veraval"},{"title":"Banda"},{"title":"Chittoor"},{"title":"Krishnanagar"},{"title":"Barrackpur"},{"title":"Lakhimpur"},{"title":"Santipur"},{"title":"Porbandar"},{"title":"Hindupur"},{"title":"Balurghat"},{"title":"Bhadravati"},{"title":"Hanumangarh"},{"title":"Moga"},{"title":"Pathankot"},{"title":"Hajipur"},{"title":"Sasaram"},{"title":"Habra"},{"title":"Bid"},{"title":"Mohali"},{"title":"Burari"},{"title":"Beawar"},{"title":"Abohar"},{"title":"Tiruvannamalai"},{"title":"Jamuria"},{"title":"Kaithal"},{"title":"Godhra"},{"title":"Bhuj"},{"title":"Robertson Pet"},{"title":"Shillong"},{"title":"Rewari"},{"title":"Hazaribag"},{"title":"Bhimavaram"},{"title":"Mandsaur"},{"title":"Chas"},{"title":"Rudrapur"},{"title":"Chitradurga"},{"title":"Kumbakonam"},{"title":"Dibrugarh"},{"title":"Kolar"},{"title":"Chhindwara"},{"title":"Bankura"},{"title":"Mandya"},{"title":"Dehri"},{"title":"Raigarh"},{"title":"Madanapalle"},{"title":"Nalgonda"},{"title":"Hathras"},{"title":"Malerkotla"},{"title":"Siwan"},{"title":"Chhattarpur"},{"title":"Hassan"},{"title":"Lalitpur"},{"title":"Gondiya"},{"title":"North Barrackpur"},{"title":"Bettiah"},{"title":"Palakkad"},{"title":"Rajapalayam"},{"title":"Botad"},{"title":"Modinagar"},{"title":"Deoria"},{"title":"Raniganj"},{"title":"Palwal"},{"title":"Khanna"},{"title":"Neemuch"},{"title":"Pilibhit"},{"title":"Mustafabad"},{"title":"Hardoi"},{"title":"Guntakal"},{"title":"Pithampur"},{"title":"Motihari"},{"title":"Dhaulpur"},{"title":"Srikakulam"},{"title":"Nabadwip"},{"title":"Patan"},{"title":"Jagdalpur"},{"title":"Udupi"},{"title":"Basirhat"},{"title":"Damoh"},{"title":"Halisahar"},{"title":"Jagadhri"},{"title":"Rishra"},{"title":"Kurichi"},{"title":"Dimapur"},{"title":"Palanpur"},{"title":"Dharmavaram"},{"title":"Gokal Pur"},{"title":"Kashipur"},{"title":"Ashokenagar Kalyangarh"},{"title":"Baidyabati"},{"title":"Sawai Madhopur"},{"title":"Puruliya"},{"title":"Mandoli"},{"title":"Mainpuri"},{"title":"Kanchrapara"},{"title":"Satara"},{"title":"Churu"},{"title":"Madavaram"},{"title":"Gangapur"},{"title":"Dabgram"},{"title":"Darjiling"},{"title":"Barshi"},{"title":"Etah"},{"title":"Jhunjhunun"},{"title":"Chikmagalur"},{"title":"Jetpur Navagadh"},{"title":"Roorkee"},{"title":"Gudivada"},{"title":"Baleshwar"},{"title":"Baran"},{"title":"Hoshangabad"},{"title":"Nagaon"},{"title":"Pudukkottai"},{"title":"Adilabad"},{"title":"Hosur"},{"title":"Muktsar"},{"title":"Yavatmal"},{"title":"Titagarh"},{"title":"Barnala"},{"title":"Chittaurgarh"},{"title":"Narasaraopet"},{"title":"Dum Dum"},{"title":"Basti"},{"title":"Valsad"},{"title":"Ambur"},{"title":"Giridih"},{"title":"Chandausi"},{"title":"Gonda"},{"title":"Bally Town"},{"title":"Kalol"},{"title":"Bagaha"},{"title":"Ambikapur"},{"title":"Achalpur"},{"title":"Gondal"},{"title":"Bagalkot"},{"title":"Osmanabad"},{"title":"Akbarpur"},{"title":"Champdani"},{"title":"Deesa"},{"title":"Khurja"},{"title":"Nandurbar"},{"title":"Azamgarh"},{"title":"Ghazipur"},{"title":"Delhi Cantonment"},{"title":"Firozpur"},{"title":"Baripada"},{"title":"Mughalsarai"},{"title":"Anantnag"},{"title":"Sehore"},{"title":"Bongaon"},{"title":"Kanpur Cantonment"},{"title":"Khardaha"},{"title":"Tadpatri"},{"title":"Port Blair"},{"title":"Sultanpur"},{"title":"Bhadrak"},{"title":"Shikohabad"},{"title":"Jalpaiguri"},{"title":"Shamli"},{"title":"Karaikkudi"},{"title":"Khargone"},{"title":"Wardha"},{"title":"Ranibennur"},{"title":"Kishanganj"},{"title":"Neyveli"},{"title":"Amreli"},{"title":"Suryapet"},{"title":"Gangawati"},{"title":"Hindaun"},{"title":"Jamalpur"},{"title":"Bhiwadi"},{"title":"Ballia"},{"title":"Bansberia"},{"title":"Tadepalligudem"},{"title":"Miryalaguda"},{"title":"Baraut"},{"title":"Udgir"},{"title":"Betul"},{"title":"Bundi"},{"title":"Jehanabad"},{"title":"Ambala Sadar"},{"title":"Nagapattinam"},{"title":"Nagaur"},{"title":"Buxar"},{"title":"Seoni"},{"title":"Greater Noida"},{"title":"Hinganghat"},{"title":"Sujangarh"},{"title":"Bhadreswar"},{"title":"Chilakaluripet"},{"title":"Kasganj"},{"title":"Kalyani"},{"title":"Gangtok"},{"title":"Datia"},{"title":"Nagda"},{"title":"Banswara"},{"title":"Kapurthala"}, {"title": "Remote"}, {"title": "PAN INDIA"}, {"title": "Ratnagiri"}, {"title": "Goa"}, {"title": "Panaji"}],
        invoiceFile: null,
        existingInvoiceFilePath: null,
        candidateListError: '',
        filter: '',
        modalCandidateListFields : [
          { key: 'candidate_id', label: 'Selected', sortable: true },
          { key: 'candidate_name', label: 'Name', sortable: true },
          { key: 'position_name', label: 'Position Name', sortable: true },
          { key: 'client', label: 'Client', sortable: true },
          { key: 'revenue', label: 'Revenue', sortable: true },
          { key: 'joining_date', label: 'DOJ', sortable: true },
        ],
        invoiceCandidateList: [],
        rowSelected: false,
        selected: [],
        totalBaseAmount: 0.0,
        totalGST: 0.0,
        totalAmountWithGST: 0.0,
        totalAmountWithGSTTDS: 0.0,
        validateGSTError: '',
        invoiceDueDate: null,
        invoiceEmailDate: null,
        invoicePaidAmount: null,
        invoicePaymentAmount: null,
        invoicePaymentDate: null,
        invoicePaymentComment: '',
        invoiceVertical: null,
        invoiceSattvaGST: null,
        invoiceBillingMonth: null,
        invoiceCategory: null,
        invoiceBaseAmount: null,
        invoicePONumber: null,
        invoiceDescription: null,
        r1Filed: null,
        invoiceComment: '',
        r1FiledOptions: [
          { text: 'Select...', value: null },
          { text: 'Yes', value: 'Yes' },
          { text: 'No', value: 'No' }
        ],
        // Cancellation
        cancellationReason: null,
        cancellationComment: '',
        cancellationReasonOptions: [
          { value: null, text: 'Select reason...' },
          { value: 'Early Attrition', text: 'Early Attrition' },
          { value: 'Invoice Filed and cannot be Amended', text: 'Invoice Filed and cannot be Amended' },
          { value: 'Bad Debt', text: 'Bad Debt' },
        ],
        // GST Hold (Part Paid)
        isGSTHold: false,
        gstHoldReason: '',
        // Revision workflow
        revisionType: null,
        amountChangeType: null,
        amendInvoice: null,
        revisionReason: '',
        revisedInvoiceFile: null,
        r1UploadFile: null,
        noteFile: null,
        noteDate: null,
        noteSentToClient: null,
        noteEInvoiceDone: null,
        // Populated via GET /invoice/:id/revision/history
        revisionHistory: [],
        revisionHistoryLoaded: false,
        // 'revision-history-api' | 'missing-route' | 'update-response' | 'empty' | 'error'
        revisionHistorySource: null,
        invoiceRecord: null,
        invoiceTimeline: [],
        invoiceTimelineLoading: false,
        invoiceTimelineMeta: null,
        error: {
          invoiceId: '',
          invoiceDate: '',
          invoiceLocation: '',
          invoiceGST: '',
          invoiceStatus: '',
          invoicePaidDate: '',
          invoiceClient: '',
          invoiceClientGST: '',
          invoiceFile: '',
          invoiceDescription: '',
          invoicePONumber: '',
          invoiceCategory: '',
          invoiceBaseAmount: '',
          invoicePaidAmount: '',
          r1Filed: '',
          invoiceEmailDate: '',
          invoiceDueDate: '',
        }, submitError: '',
        blink: null,
        pageLoading: true,
        loadError: '',
        showMobilePreview: false,
        lineItems: [],
        lineItemsFromApi: false,
        gstRatePercent: 18,
        tdsApplicable: true,
        tdsPercentage: 10,
        amountsLineItemFields: [
          { key: 'srNo', label: '#', thStyle: { width: '40px' } },
          { key: 'itemName', label: 'Description' },
          { key: 'quantity', label: 'Qty', thStyle: { width: '80px' } },
          { key: 'unitPrice', label: 'Rate', thStyle: { width: '100px' } },
          { key: 'amount', label: 'Amount', thStyle: { width: '120px' } },
          { key: 'actions', label: '', thStyle: { width: '48px' } },
        ],
        previewPanelFit: { scale: 1, widthPx: 794, heightPx: 1123 },
        previewMobileFit: { scale: 1, widthPx: 794, heightPx: 1123 },
        _a4PreviewObservers: [],
        selectedChangeTypes: [],
        activeChangeType: null,
        sectionStates: {},
        changeTypeOptions: [
          { id: 'status', label: 'Status', icon: 'ActivityIcon', hint: 'Raised, Paid, Revised, Cancelled, Part Paid…' },
          { id: 'basic', label: 'Basic details', icon: 'FileTextIcon', hint: 'Date, category, location, description' },
          { id: 'client', label: 'Client / billing', icon: 'UsersIcon', hint: 'Client, GST type, client GST' },
          { id: 'amounts', label: 'Amounts & GST', icon: 'DollarSignIcon', hint: 'Line items, base amount, totals' },
          { id: 'payment', label: 'Payment', icon: 'CreditCardIcon', hint: 'Paid date, paid amount, due date' },
          { id: 'revision', label: 'Revision', icon: 'RefreshCwIcon', hint: 'R1, revision type, revised uploads' },
          { id: 'document', label: 'Documents', icon: 'PaperclipIcon', hint: 'Invoice PDF, revised invoice file' },
          { id: 'emailDates', label: 'Email & dates', icon: 'CalendarIcon', hint: 'Email date, due date' },
          { id: 'other', label: 'Comment / other', icon: 'MessageSquareIcon', hint: 'Notes and general comments' },
        ],
        }
    },
    components: {
        BRow,
        BCol,
        BCard,
        BCardHeader,
        BCardBody,
        BForm,
        BLink,
        BFormGroup,
        BInputGroup,
        BInputGroupAppend,
        BFormInput,
        BModal,
        BTable,
        BButton,
        BFormFile,
        vSelect,
        flatPickr,
        BFormInvalidFeedback,
        ToastificationContent,
        BFormSelect,
        BFormTextarea,
        BAlert,
        BFormCheckbox,
        BFormRadioGroup,
        BSpinner,
        BBadge,
        BCollapse,
        InvoiceTimelineCard,
        InvoiceVersionsSection,
        InvoiceRevisionHistoryModal,
        GenericInvoicePreview,
        WizardSectionHeader,
    },
    computed: {
      lineItemTitleOptions() {
        return buildLineItemTitleOptions({ lineItems: this.lineItems })
      },
      hasSelectedChangeTypes() {
        return this.selectedChangeTypes.length > 0
      },
      completedChangeTypes() {
        return this.changeTypeOptions
          .map(o => o.id)
          .filter(id => this.selectedChangeTypes.includes(id) && this.sectionStates[id] === 'completed')
      },
      availableChangeTypeOptions() {
        return this.changeTypeOptions.filter(o => !this.selectedChangeTypes.includes(o.id))
      },
      allSectionsCompleted() {
        if (!this.hasSelectedChangeTypes) return false
        return this.selectedChangeTypes.every(id => this.sectionStates[id] === 'completed')
      },
      changePickerHeading() {
        if (!this.hasSelectedChangeTypes) return 'What changes do you want to make?'
        if (this.activeChangeType) return `Editing: ${this.changeTypeLabel(this.activeChangeType)}`
        return 'What else do you want to change?'
      },
      changePickerHint() {
        if (this.activeChangeType) return ''
        if (!this.hasSelectedChangeTypes) return 'Pick a change type — one section expands at a time.'
        if (this.availableChangeTypeOptions.length) return 'Add another change type, or save when finished.'
        return 'Review summaries above, then save your changes.'
      },
      hideActiveSectionHeader() {
        return this.activeChangeType === 'revision' && this.invoiceStatus === 'Revised'
      },
      showContextR1Badge() {
        if (this.r1Filed) return true
        if (this.activeChangeType === 'revision') return true
        if (this.invoiceStatus === 'Revised' && this.selectedChangeTypes.includes('revision')) return true
        return false
      },
      contextR1Badge() {
        if (!this.showContextR1Badge) return ''
        if (this.r1Filed === 'Yes') return 'R1 Filed'
        if (this.r1Filed === 'No') return 'R1 NOT Filed'
        return 'R1 workflow'
      },
      contextR1BadgeVariant() {
        if (this.r1Filed === 'Yes') return 'light-success'
        if (this.r1Filed === 'No') return 'light-warning'
        return 'light-secondary'
      },
      showPaymentFieldsActive() {
        return this.activeChangeType === 'payment' && this.selectedChangeTypes.includes('payment')
      },
      showRevisionFieldsActive() {
        return this.activeChangeType === 'revision'
      },
      showStatusSection() {
        return this.selectedChangeTypes.includes('status')
      },
      showBasicSection() {
        return this.selectedChangeTypes.includes('basic')
      },
      showClientSection() {
        return this.selectedChangeTypes.includes('client')
      },
      showAmountsSection() {
        return this.selectedChangeTypes.includes('amounts')
      },
      isCandidateCategory() {
        return this.invoiceCategory === 'Recruitment' || this.invoiceCategory === 'Contractual'
      },
      showAmountsLineItemsEditor() {
        if (this.isCandidateCategory && this.showInvoiceCandidateList) return false
        return this.lineItems && this.lineItems.length > 0
      },
      showBaseAmountInput() {
        if (this.showAmountsLineItemsEditor) return false
        if (this.isCandidateCategory && this.showInvoiceCandidateList) return false
        return true
      },
      showAmountsTotals() {
        return this.parseAmount(this.totalBaseAmount) > 0
          || this.showAmountsLineItemsEditor
          || this.showInvoiceCandidateList
          || (this.invoiceBaseAmount !== null && this.invoiceBaseAmount !== '')
      },
      showPaymentSection() {
        return this.selectedChangeTypes.includes('payment')
          || (this.showStatusSection && this.isPaidOrPartPaidStatus)
      },
      showEmailDatesSection() {
        return this.selectedChangeTypes.includes('emailDates')
      },
      showDocumentSection() {
        return this.selectedChangeTypes.includes('document')
      },
      showRevisionSection() {
        return this.selectedChangeTypes.includes('revision')
          || (this.selectedChangeTypes.includes('status') && this.invoiceStatus === 'Revised')
      },
      invoiceGSTSelectOptions() {
        return this.invoiceGSTOptions.map(o => ({ value: o.title, text: o.label || o.title }))
      },
      splitGstAmounts() {
        const gst = this.parseAmount(this.totalGST)
        const sgst = parseFloat((gst / 2).toFixed(2))
        return { cgst: sgst, sgst: parseFloat((gst - sgst).toFixed(2)) }
      },
      showOtherSection() {
        return this.selectedChangeTypes.includes('other')
      },
      showR1Fields() {
        if (!this.showStatusSection || !this.invoiceStatus) return false
        if (this.invoiceStatus === 'Revised') return false
        return !['Raised', 'Unpaid', 'Part Paid', 'Partially Paid', 'Cancelled'].includes(this.invoiceStatus)
      },
      hasInvoiceFile() {
        return this.invoiceFile instanceof File || (this.existingInvoiceFilePath && typeof this.existingInvoiceFilePath === 'string')
      },
      noteDateError() {
        if (!this.noteDate || !this.invoiceDate) return ''
        if (this.noteDate > this.invoiceDate) {
          const label = this.amountChangeType === 'increase' ? 'Debit Note' : 'Credit Note'
          return `${label} date (${this.noteDate}) cannot be later than Invoice Date (${this.invoiceDate}).`
        }
        return ''
      },
      normalizedInvoiceStatus() {
        const raw = String(this.invoiceStatus || '').trim().toLowerCase().replace(/\s+/g, ' ')
        if (raw === 'partially paid') return 'Part Paid'
        if (raw === 'part paid') return 'Part Paid'
        if (raw === 'paid') return 'Paid'
        return this.invoiceStatus
      },
      isPaidStatus() {
        return this.normalizedInvoiceStatus === 'Paid'
      },
      isPartPaidStatus() {
        return this.normalizedInvoiceStatus === 'Part Paid'
      },
      isPaidOrPartPaidStatus() {
        return this.isPaidStatus || this.isPartPaidStatus
      },
      previewTemplateForDisplay() {
        const record = this.invoiceRecord || {}
        const style = record.layout_style || (record.config && record.config.layout_style) || DEFAULT_TEMPLATE.layout.style
        const cols = this.previewLineItemColumns
        return {
          ...DEFAULT_TEMPLATE,
          layout: { ...(DEFAULT_TEMPLATE.layout || {}), style: style || 'bajaj' },
          lineItemColumns: cols.length ? cols : DEFAULT_TEMPLATE.lineItemColumns,
        }
      },
      previewLineItemColumns() {
        const record = this.invoiceRecord || {}
        const raw = record.line_item_column_config
          || (record.config && record.config.line_item_column_config)
          || null
        if (!raw) return []
        if (Array.isArray(raw)) return raw
        try {
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
          return Array.isArray(parsed) ? parsed : []
        } catch (_) {
          return []
        }
      },
      previewBillFrom() {
        const s = this.invoiceRecord || {}
        return {
          companyName: s.sattva_company_name || s.company_name || 'Sattva Human Private Limited',
          address: s.sattva_address || 'Office, 1st Floor, Ruia Chambers, Hadapsar, Pune 411028',
          gstin: (this.invoiceSattvaGST || s.sattva_gst || '27ABDCS0586N1ZT').toUpperCase(),
          pan: s.sattva_pan || 'ABDCS0586N',
          sacCode: s.sac_code || '',
          lutNo: s.sattva_lut_no || s.lut_no || '',
        }
      },
      previewBillTo() {
        return {
          name: this.invoiceClient || 'Client',
          address: (this.invoiceRecord && this.invoiceRecord.client_address) || this.invoiceLocation || '',
          gstin: (this.invoiceClientGST || '').toUpperCase(),
        }
      },
      previewInvoiceMeta() {
        return {
          invoiceId: this.invoiceId || 'SH000',
          invoiceDate: this.invoiceDate || '',
          placeOfSupply: this.invoiceLocation || '',
          poNumber: this.invoicePONumber || '',
          packageCode: (this.invoiceRecord && this.invoiceRecord.package_code) || '',
          billingMonth: this.invoiceBillingMonth || '',
          status: this.invoiceStatus || '',
        }
      },
      previewInvoiceStatus() {
        return this.invoiceStatus || ''
      },
      previewLineItemsForGeneric() {
        if (this.lineItems && this.lineItems.length) return this.lineItems
        if (this.invoiceCandidateList && this.invoiceCandidateList.length) {
          return this.invoiceCandidateList.map((c, i) => ({
            srNo: i + 1,
            itemName: c.candidate_name || c.position_name || 'Recruitment',
            candidateName: c.candidate_name,
            positionName: c.position_name,
            amount: this.parseAmount(c.base_amount),
            baseAmount: this.parseAmount(c.base_amount),
          }))
        }
        const base = this.parseAmount(this.invoiceBaseAmount || this.totalBaseAmount)
        if (base > 0 || this.invoiceDescription) {
          return [{
            srNo: 1,
            itemName: this.invoiceDescription || this.invoiceCategory || 'Services',
            quantity: 1,
            unitPrice: base,
            amount: base,
          }]
        }
        return []
      },
      previewTotalsForGeneric() {
        const gst = this.parseAmount(this.totalGST)
        const gstType = this.invoiceGST
        let sgst = 0
        let cgst = 0
        let igst = 0
        if (gstType === 'IGST') {
          igst = gst
        } else if (gstType !== 'SEZ') {
          sgst = parseFloat((gst / 2).toFixed(2))
          cgst = gst - sgst
        }
        return {
          subtotal: this.parseAmount(this.totalBaseAmount),
          sgst,
          cgst,
          igst,
          total: this.parseAmount(this.totalAmountWithGSTTDS || this.totalAmountWithGST),
          reverseCharge: 0,
        }
      },
      previewBankDetailsForGeneric() {
        const s = this.invoiceRecord || {}
        return {
          bankName: s.bank_name || 'HDFC Bank',
          accountName: s.account_name || 'Sattva Human Private Limited',
          accountNumber: s.account_number || '50200046864261',
          ifscCode: s.ifsc_code || 'HDFC0000486',
          accountType: s.account_type || 'Current Account',
        }
      },
      previewPanelFitStyle() { return this.a4FitBoxStyle(this.previewPanelFit) },
      previewPanelScalerStyle() { return this.a4ScalerStyle(this.previewPanelFit) },
      previewMobileFitStyle() { return this.a4FitBoxStyle(this.previewMobileFit) },
      previewMobileScalerStyle() { return this.a4ScalerStyle(this.previewMobileFit) },
    },
    watch: {
      invoiceClient: function(newClient) {
        this.invoiceCandidateList = !this.invoiceClient ? [] : this.invoiceCandidateList
      },
      invoiceStatus(status) {
        const key = String(status || '').trim().toLowerCase().replace(/\s+/g, ' ')
        if (key === 'partially paid') {
          this.invoiceStatus = 'Part Paid'
          return
        }
        if (key === 'paid') {
          const total = this.parseAmount(this.totalAmountWithGSTTDS || this.totalAmountWithGST)
          if ((!this.invoicePaidAmount || this.parseAmount(this.invoicePaidAmount) === 0) && total > 0) {
            this.invoicePaidAmount = total
          }
        }
        if (status === 'Revised' && this.showStatusSection) {
          this.ensureChangeType('revision')
        }
      },
      showMobilePreview(val) {
        if (!val) return
        this.$nextTick(() => {
          this.observeA4Stage('previewMobileStage', 'previewMobileFit', 'previewMobileRoot')
        })
      },
    },
    directives: {
      'b-modal': VBModal,
      'b-toggle': VBToggle,
      Ripple,
    },
    
    created() {
      this.invoiceId = resolveInvoiceApiKey(this.$route.params.id)
        || resolveInvoiceApiKey(this.$route.query.invoice_id)
        || resolveInvoiceApiKey(this.$route.query.invoice_number)
        || resolveInvoiceApiKey(this.$route.query.invoiceId)
        || resolveInvoiceApiKey(this.$route.query.id)
      if (this.invoiceId) {
        try { this.invoiceId = decodeURIComponent(this.invoiceId) } catch (e) { /* already decoded */ }
      }
      this.pageLoading = true
      this.loadError = ''
      clientService.getClients().then(res => {
        this.clients = res.data
      })
      locationService.getAll().then(res => {
        this.locations = res.data
      })
      invoiceService.getById(this.invoiceId).then(async res => {
          if (res && res.status === 'OK') {
            this.invoiceRecord = res.data
            this.invoiceId = res.data.invoice_id
            this.invoiceDate = res.data.invoice_date
            this.invoiceStatus = res.data.status
            this.invoiceCategory = res.data.category ? res.data.category : null
            this.invoiceDescription = res.data.description ? res.data.description : null
            this.invoicePONumber = res.data.po_number ? res.data.po_number : null
            this.invoiceEmailDate = res.data.email_date ? res.data.email_date : null
            this.invoiceDueDate = res.data.due_date ? res.data.due_date : null
            this.invoiceLocation = res.data.location
            this.invoiceGST = res.data.gst_type
            this.invoiceClient = res.data.client
            this.invoiceClientGST = res.data.client_gst ? res.data.client_gst.toUpperCase() : ''
            this.existingInvoiceFilePath = res.data.file || null
            this.invoiceFile = null
            this.invoicePaidDate = res.data.paid_date
            this.invoiceBaseAmount = res.data.base_amount != null ? res.data.base_amount : null
            const loadedPaid = res.data.paid_amount
            this.invoicePaidAmount = (loadedPaid === null || loadedPaid === undefined || loadedPaid === '')
              ? null
              : loadedPaid
            if (String(this.invoiceStatus || '').toLowerCase().replace(/\s+/g, ' ') === 'partially paid') {
              this.invoiceStatus = 'Part Paid'
            }
            this.invoicePaymentAmount = res.data.payment_amount || null
            this.invoicePaymentDate = res.data.payment_date || null
            this.invoicePaymentComment = res.data.payment_comment || ''
            this.invoiceVertical = res.data.vertical ? res.data.vertical : null
            this.invoiceSattvaGST = res.data.sattva_gst ? res.data.sattva_gst : null
            this.invoiceBillingMonth = res.data.billing_month ? res.data.billing_month : null
            this.totalBaseAmount = res.data.base_amount
            this.totalGST = res.data.gst
            this.totalAmountWithGST = res.data.amount_with_gst
            this.totalAmountWithGSTTDS = res.data.total_amount
            const rawLineItems = res.data.line_items || res.data.lineItems
            this.lineItemsFromApi = Array.isArray(rawLineItems) && rawLineItems.length > 0
            this.invoiceCandidateList = res.candidates ? res.candidates : []
            this.lineItems = this.parseLineItemsFromRecord(res.data, res.candidates)
            if (res.data.tds_applicable !== undefined && res.data.tds_applicable !== null) {
              this.tdsApplicable = res.data.tds_applicable === true
                || res.data.tds_applicable === 'true'
                || res.data.tds_applicable === 1
                || res.data.tds_applicable === '1'
            } else {
              const withGst = this.parseAmount(res.data.amount_with_gst)
              const totalAmt = this.parseAmount(res.data.total_amount)
              this.tdsApplicable = withGst > 0 && totalAmt > 0 && totalAmt < withGst
            }
            if (res.data.tds_percentage != null && res.data.tds_percentage !== '') {
              this.tdsPercentage = parseFloat(res.data.tds_percentage) || 10
            }
            const loadedBase = this.parseAmount(res.data.base_amount)
            const loadedGst = this.parseAmount(res.data.gst)
            if (loadedBase > 0 && loadedGst > 0) {
              this.gstRatePercent = parseFloat(((loadedGst / loadedBase) * 100).toFixed(2))
            } else if (res.data.gst_rate != null && res.data.gst_rate !== '') {
              this.gstRatePercent = parseFloat(res.data.gst_rate) || 18
            }

            if (this.invoiceStatus && this.invoiceStatus !== 'Raised') {
              this.r1Filed = res.data.r1_filed || null
              this.invoiceComment = res.data.comment || ''
            }

            this.getFile()
            if (this.invoiceCandidateList.length > 0) {
              this.showInvoiceCandidateList = true
            }
            await this.loadRevisionHistory()
            await this.loadInvoiceTimeline()
            this.applyReviseRouteQuery()
            this.calculateTotalBaseAmount()
            this.pageLoading = false
            this.$nextTick(() => {
              this.observeA4Stage('previewPanelStage', 'previewPanelFit', 'previewPanelRoot')
            })
          } else if (res && ((res.status === 401) || (res.status === 403))) {
            window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
            localStorage.clear()
            this.$router.push({ name: 'auth-login' })
          } else if (res && (res.status === 400 || res.status === 404)) {
            const msg = (res.data && res.data.message) || 'Invoice not found.'
            this.loadError = msg
            this.pageLoading = false
            this.$toast({
              component: ToastificationContent,
              props: {
                title: msg,
                icon: 'AlertCircleIcon',
                variant: 'danger'
              }
            })
          } else {
            this.loadError = (res && res.data && res.data.message) || 'Failed to load invoice.'
            this.pageLoading = false
          }
      }).catch(() => {
        this.loadError = 'Failed to load invoice. Please try again.'
        this.pageLoading = false
      })
    },
    mounted() {
      this.observeA4Stage('previewPanelStage', 'previewPanelFit', 'previewPanelRoot')
    },
    beforeDestroy() {
      (this._a4PreviewObservers || []).forEach(entry => {
        try { entry.ro.disconnect() } catch (_) { /* ignore */ }
      })
      this._a4PreviewObservers = []
    },
    methods: {
        parseLineItemsFromRecord(data, candidates) {
          const raw = data.line_items || data.lineItems
          if (Array.isArray(raw) && raw.length) {
            return raw.map((row, i) => this.normalizeLineItemRow(row, i))
          }
          if (Array.isArray(candidates) && candidates.length) {
            return candidates.map((c, i) => this.normalizeLineItemRow({
              itemName: c.candidate_name || c.position_name || 'Recruitment',
              candidateName: c.candidate_name,
              positionName: c.position_name,
              amount: c.base_amount,
              baseAmount: c.base_amount,
            }, i))
          }
          const base = this.parseAmount(data.base_amount)
          if (base > 0) {
            return [this.normalizeLineItemRow({
              itemName: data.description || data.category || 'Services',
              quantity: 1,
              unitPrice: base,
              amount: base,
            }, 0)]
          }
          return []
        },
        normalizeLineItemRow(row, index) {
          const amount = this.parseAmount(row.amount != null ? row.amount : (row.baseAmount != null ? row.baseAmount : row.base_amount))
          const quantity = row.quantity != null && row.quantity !== '' ? this.parseAmount(row.quantity) : 1
          const unitPrice = row.unitPrice != null && row.unitPrice !== ''
            ? this.parseAmount(row.unitPrice)
            : (row.unit_price != null ? this.parseAmount(row.unit_price) : (quantity ? amount / quantity : amount))
          return {
            srNo: index + 1,
            itemName: row.itemName || row.item_name || row.title || row.description || '',
            title: row.title || row.itemName || row.item_name || row.description || '',
            quantity,
            unitPrice,
            amount,
            sacCode: row.sacCode || row.sac_code || '',
            candidateName: row.candidateName || row.candidate_name,
            positionName: row.positionName || row.position_name,
          }
        },
        reindexLineItems() {
          this.lineItems = this.lineItems.map((row, i) => ({ ...row, srNo: i + 1 }))
        },
        addLineItem() {
          const nextIndex = this.lineItems.length
          this.lineItems.push(this.normalizeLineItemRow({
            itemName: this.invoiceDescription || this.invoiceCategory || 'Services',
            quantity: 1,
            unitPrice: 0,
            amount: 0,
          }, nextIndex))
          this.calculateTotalBaseAmount()
        },
        switchToLineItems() {
          const base = this.parseAmount(this.invoiceBaseAmount || this.totalBaseAmount)
          this.lineItems = [this.normalizeLineItemRow({
            itemName: this.invoiceDescription || this.invoiceCategory || 'Services',
            quantity: 1,
            unitPrice: base,
            amount: base,
          }, 0)]
          this.calculateTotalBaseAmount()
        },
        removeLineItem(index) {
          if (this.lineItems.length <= 1) return
          this.lineItems.splice(index, 1)
          this.reindexLineItems()
          this.calculateTotalBaseAmount()
        },
        onLineItemFieldChange(index) {
          const row = this.lineItems[index]
          if (!row) return
          const qty = this.parseAmount(row.quantity)
          const rate = this.parseAmount(row.unitPrice)
          if (qty > 0 && rate >= 0) {
            row.amount = parseFloat((qty * rate).toFixed(2))
          }
          this.calculateTotalBaseAmount()
        },
        a4FitBoxStyle(fit) {
          const scale = (fit && fit.scale) || 1
          const w = (fit && fit.widthPx) || 794
          const h = (fit && fit.heightPx) || 1123
          return {
            width: `${Math.round(w * scale)}px`,
            height: `${Math.round(h * scale)}px`,
            margin: '0 auto',
            position: 'relative',
          }
        },
        a4ScalerStyle(fit) {
          const scale = (fit && fit.scale) || 1
          const w = (fit && fit.widthPx) || 794
          return {
            width: `${w}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }
        },
        observeA4Stage(stageRef, fitKey, sheetRef) {
          if (typeof ResizeObserver === 'undefined') return
          this._a4PreviewObservers = (this._a4PreviewObservers || []).filter(entry => {
            if (entry.stageRef === stageRef) {
              try { entry.ro.disconnect() } catch (_) { /* ignore */ }
              return false
            }
            return true
          })
          const stageEl = this.$refs[stageRef]
          if (!stageEl) return
          const ro = new ResizeObserver(() => {
            this.syncA4PreviewFit(stageRef, fitKey, sheetRef)
          })
          ro.observe(stageEl)
          const sheetComp = this.$refs[sheetRef]
          const sheetEl = sheetComp && sheetComp.$el
          if (sheetEl) ro.observe(sheetEl)
          this._a4PreviewObservers.push({ ro, stageRef, fitKey, sheetRef })
          this.syncA4PreviewFit(stageRef, fitKey, sheetRef)
        },
        syncA4PreviewFit(stageRef, fitKey, sheetRef) {
          const stageEl = this.$refs[stageRef]
          const sheetComp = this.$refs[sheetRef]
          const sheetEl = sheetComp && sheetComp.$el
          if (!stageEl || !sheetEl) return
          const stageW = stageEl.clientWidth
          if (stageW <= 0) return
          const widthPx = sheetEl.offsetWidth || 794
          const heightPx = sheetEl.offsetHeight || 1123
          const available = Math.max(120, stageW - 8)
          const scale = Math.min(1, available / widthPx)
          const next = { scale, widthPx, heightPx }
          const prev = this[fitKey]
          if (
            prev
            && Math.abs(prev.scale - next.scale) < 0.001
            && prev.widthPx === next.widthPx
            && prev.heightPx === next.heightPx
          ) {
            return
          }
          this[fitKey] = next
        },
        applyReviseRouteQuery() {
          if (String(this.$route.query.revise || '') !== '1') return
          this.invoiceStatus = REVISED_STATUS
          const r1 = this.$route.query.r1_filed
          if (r1 === 'Yes' || r1 === 'No') {
            this.r1Filed = r1
          }
          this.selectChangeType('revision')
        },
        /**
         * Ensure live invoices.status is Revised after a revise PUT.
         * Multipart may return OK while leaving status unchanged — JSON status
         * write (same path as dashboard Paid/etc.) is the fallback.
         */
        async ensureRevisedStatusPersisted(updateRes) {
          const returned = extractUpdatedInvoiceStatus(updateRes)
          if (isRevisedStatusValue(returned)) {
            this.invoiceStatus = REVISED_STATUS
            if (this.invoiceRecord) this.$set(this.invoiceRecord, 'status', REVISED_STATUS)
            return { ok: true, status: REVISED_STATUS, source: 'update-response' }
          }

          const userData = JSON.parse(localStorage.getItem('userData') || '{}')
          const patchRes = await invoiceService.updateInvoice(this.invoiceId, {
            status: REVISED_STATUS,
            r1_filed: this.r1Filed || '',
            updated_by: userData.emp_id || '',
            comment: this.invoiceComment || '',
          })
          if (!patchRes || patchRes.status !== 'OK') {
            return {
              ok: false,
              message: formatInvoiceApiError(
                patchRes,
                'Revision save returned OK but invoices.status was not set to Revised, and the follow-up status update failed.',
              ),
            }
          }

          let live = extractUpdatedInvoiceStatus(patchRes)
          if (!isRevisedStatusValue(live)) {
            try {
              const got = await invoiceService.getById(this.invoiceId)
              if (got && got.status === 'OK' && got.data) {
                live = got.data.status
              }
            } catch (_) { /* ignore */ }
          }

          if (!isRevisedStatusValue(live)) {
            return {
              ok: false,
              message: live != null
                ? `Server left invoice status as "${live}" instead of Revised. Backend must UPDATE invoices.status when PUT /invoice/update/:id receives status=Revised.`
                : 'Could not verify invoices.status after Revised update. Backend must set status=Revised on PUT /invoice/update/:id and return the updated invoice (or support GET /invoice/:id).',
            }
          }

          this.invoiceStatus = REVISED_STATUS
          if (this.invoiceRecord) this.$set(this.invoiceRecord, 'status', REVISED_STATUS)
          return { ok: true, status: REVISED_STATUS, source: 'verified' }
        },
        changeTypeIcon(id) {
          const opt = this.changeTypeOptions.find(o => o.id === id)
          return opt ? opt.icon : 'EditIcon'
        },
        isChangeTypeSelected(id) {
          return this.selectedChangeTypes.includes(id)
        },
        changeTypeLabel(id) {
          const opt = this.changeTypeOptions.find(o => o.id === id)
          return opt ? opt.label : id
        },
        ensureChangeType(id) {
          if (!this.selectedChangeTypes.includes(id)) {
            this.selectedChangeTypes = [...this.selectedChangeTypes, id]
          }
        },
        selectChangeType(id) {
          if (id === 'revision') {
            this.invoiceStatus = REVISED_STATUS
          }
          if (!this.selectedChangeTypes.includes(id)) {
            this.selectedChangeTypes = [...this.selectedChangeTypes, id]
          }
          this.activeChangeType = id
          this.$set(this.sectionStates, id, 'active')
        },
        expandSection(id) {
          this.activeChangeType = id
          this.$set(this.sectionStates, id, 'active')
        },
        cancelActiveSection() {
          const id = this.activeChangeType
          if (!id) return
          if (this.sectionStates[id] !== 'completed') {
            this.selectedChangeTypes = this.selectedChangeTypes.filter(t => t !== id)
            this.$delete(this.sectionStates, id)
          }
          this.activeChangeType = null
        },
        completeSection(id) {
          if (!id) return
          if (!this.validateSections([id])) return

          this.$set(this.sectionStates, id, 'completed')
          this.activeChangeType = null

          this.$nextTick(() => {
            if (id === 'status' && this.invoiceStatus === 'Revised' && !this.selectedChangeTypes.includes('revision')) {
              this.selectChangeType('revision')
            } else if (id === 'status' && this.isPaidOrPartPaidStatus && !this.selectedChangeTypes.includes('payment')) {
              this.selectChangeType('payment')
            }
          })
        },
        getSectionSummary(id) {
          switch (id) {
            case 'status': {
              let parts = [this.invoiceStatus || '—']
              if (this.invoiceStatus === 'Cancelled' && this.cancellationReason) {
                parts.push(this.cancellationReason)
              }
              if (this.showR1Fields && this.r1Filed) parts.push(`R1: ${this.r1Filed}`)
              return parts.join(' · ')
            }
            case 'basic':
              return [this.invoiceDate, this.invoiceCategory, this.invoiceLocation].filter(Boolean).join(' · ') || 'Details updated'
            case 'client':
              return [this.invoiceClient, this.invoiceGST].filter(Boolean).join(' · ') || 'Client updated'
            case 'amounts':
              return `Total: ${this.formatAmountDisplay(this.totalAmountWithGST)}${this.lineItems.length ? ` · ${this.lineItems.length} line item(s)` : ''}`
            case 'payment': {
              const paid = this.invoicePaidAmount != null && this.invoicePaidAmount !== '' ? `₹${this.invoicePaidAmount}` : ''
              const date = this.invoicePaidDate || ''
              return [paid, date].filter(Boolean).join(' · ') || 'Payment updated'
            }
            case 'revision': {
              const bits = []
              if (this.r1Filed) bits.push(`R1 ${this.r1Filed === 'Yes' ? 'Filed' : 'NOT Filed'}`)
              if (this.revisionReason) bits.push('Reason filled')
              if (this.revisedInvoiceFile instanceof File) bits.push('Invoice uploaded ✓')
              if (this.amendInvoice === 'yes' && this.r1UploadFile instanceof File) bits.push('R1 uploaded ✓')
              if (this.noteFile instanceof File) bits.push('Note uploaded ✓')
              return bits.length ? bits.join(' · ') : 'Revision details saved'
            }
            case 'document':
              if (this.invoiceFile instanceof File) return this.invoiceFile.name
              if (this.existingInvoiceFilePath) return 'Existing file kept'
              return 'Document updated'
            case 'emailDates':
              return [this.invoiceEmailDate, this.invoiceDueDate ? `Due ${this.invoiceDueDate}` : ''].filter(Boolean).join(' · ') || 'Dates updated'
            case 'other':
              return this.invoiceComment ? `${this.invoiceComment.slice(0, 60)}${this.invoiceComment.length > 60 ? '…' : ''}` : 'Comment added'
            default:
              return 'Updated'
          }
        },
        validateSections(ids) {
          this.clearValidationErrors()
          this.submitError = ''
          const unique = [...new Set(ids)]

          if (unique.includes('basic')) {
            this.error.invoiceId = !this.invoiceId || !this.invoiceId.includes('SH') ? 'Invoice Id is not valid' : ''
            this.error.invoiceDate = !this.invoiceDate ? 'Invoice Date is mandatory' : ''
            this.error.invoiceLocation = !this.invoiceLocation ? 'Invoice Location is mandatory' : ''
            this.error.invoiceCategory = !this.invoiceCategory ? 'Invoice Category is mandatory' : ''
            if (this.invoiceCategory && this.invoiceCategory !== 'Recruitment' && this.invoiceCategory !== 'Contractual') {
              this.error.invoiceDescription = !this.invoiceDescription ? 'Invoice Description is mandatory' : ''
            }
          }

          if (unique.includes('client')) {
            this.error.invoiceGST = !this.invoiceGST ? 'Invoice GST Type is mandatory' : ''
            this.error.invoiceClient = !this.invoiceClient || this.invoiceClient === 'null' ? 'Invoice Client is mandatory' : ''
            this.error.invoiceClientGST = !this.invoiceClientGST ? 'Invoice Client GST is mandatory' : ''
            if (this.invoiceClientGST) this.validateGST()
          }

          if (unique.includes('status')) {
            this.error.invoiceStatus = !this.invoiceStatus ? 'Invoice Status is mandatory' : ''
            if (this.showR1Fields) {
              this.error.r1Filed = !this.r1Filed ? 'R1 Filed status is required' : ''
            }
          }

          if (unique.includes('payment') && this.isPaidOrPartPaidStatus) {
            this.error.invoicePaidDate = !this.invoicePaidDate ? 'Invoice Paid Date is mandatory for Paid/Part Paid Invoice' : ''
            const paidAmtMissing = this.invoicePaidAmount === null || this.invoicePaidAmount === undefined || this.invoicePaidAmount === ''
            this.error.invoicePaidAmount = paidAmtMissing ? 'Paid Amount is mandatory for Paid/Part Paid Invoice' : ''
          }

          if (unique.includes('emailDates')) {
            this.error.invoiceEmailDate = !this.invoiceEmailDate ? 'Invoice Email Date is mandatory' : ''
            this.error.invoiceDueDate = !this.invoiceDueDate ? 'Invoice Due Date is mandatory' : ''
          }

          if (unique.includes('document')) {
            this.error.invoiceFile = !this.hasInvoiceFile
              ? 'Invoice File is mandatory'
              : (this.invoiceFile instanceof File && getInvoiceFileSizeError(this.invoiceFile))
                ? INVOICE_FILE_TOO_LARGE_MESSAGE
                : ''
          }

          if (unique.includes('amounts') && this.invoiceStatus !== 'Cancelled') {
            const hasBase = this.parseAmount(this.totalBaseAmount) > 0
            if (this.showAmountsLineItemsEditor && !hasBase) {
              this.error.invoiceBaseAmount = 'At least one line item must have an amount'
            } else if (this.showBaseAmountInput && !hasBase) {
              this.error.invoiceBaseAmount = 'Invoice Amount is mandatory'
            } else if (this.isCandidateCategory && this.showInvoiceCandidateList && !hasBase) {
              this.error.invoiceBaseAmount = 'At least one candidate must have a base amount'
            } else {
              this.error.invoiceBaseAmount = ''
            }
          }

          if (unique.includes('revision') && this.invoiceStatus === 'Revised' && !this.r1Filed) {
            this.error.r1Filed = 'R1 Filed status is required'
          }

          const revisedSizeError = this.revisedInvoiceFile instanceof File
            ? getInvoiceFileSizeError(this.revisedInvoiceFile)
            : null
          if (revisedSizeError) this.submitError = revisedSizeError
          if (this.noteDateError) this.submitError = this.noteDateError

          const hasFieldError = Object.values(this.error).some(v => v) || !!this.validateGSTError
          if (hasFieldError || revisedSizeError || this.noteDateError) {
            if (!this.submitError && hasFieldError) {
              this.submitError = 'Please fix the highlighted fields before continuing.'
            }
            return false
          }
          return true
        },
        toggleChangeType(id) {
          this.selectChangeType(id)
        },
        clearValidationErrors() {
          Object.keys(this.error).forEach(k => { this.error[k] = '' })
        },
        async loadInvoiceTimeline() {
          if (!this.invoiceId) return
          this.invoiceTimelineLoading = true
          try {
            const res = await invoiceTimelineService.getInvoiceTimeline(
              this.invoiceId,
              this.invoiceRecord,
              {
                revisions: this.revisionHistoryLoaded ? this.revisionHistory : undefined,
                revisionSource: this.revisionHistorySource || undefined,
              },
            )
            this.invoiceTimeline = (res && Array.isArray(res.data)) ? res.data : []
            this.invoiceTimelineMeta = (res && res._meta) || null
          } catch (_) {
            this.invoiceTimeline = []
            this.invoiceTimelineMeta = null
          } finally {
            this.invoiceTimelineLoading = false
          }
        },
        /**
         * Loads append-only rows from invoice_revisions (backend MUST implement GET …/revision/history).
         */
        async loadRevisionHistory() {
          if (!this.invoiceId) return
          try {
            const res = await enhancedInvoiceRevisionService.getRevisionHistory(this.invoiceId)
            const { rows, source } = enhancedInvoiceRevisionService.extractRevisionRows(res)
            this.revisionHistory = rows
            this.revisionHistorySource = source
          } catch (_) {
            this.revisionHistory = []
            this.revisionHistorySource = 'error'
          } finally {
            this.revisionHistoryLoaded = true
          }
        },
        /**
         * Prefer revision object returned by PUT update when history GET is empty/missing.
         * Only uses a real server-returned revision — never invents a fake row.
         */
        mergeRevisionFromUpdateResponse(res) {
          const payload = res && res.data != null ? res.data : res
          const revision = payload && (payload.revision || (payload.data && payload.data.revision))
          if (!revision || typeof revision !== 'object') return false
          const normalized = enhancedInvoiceRevisionService.normalizeRevisionRow(
            revision,
            this.revisionHistory.length,
          )
          const id = normalized.id != null ? normalized.id : normalized.revision_number
          const already = this.revisionHistory.some((r) => (
            (id != null && (r.id === id || r.revision_number === id))
            || (normalized.revision_number != null && r.revision_number === normalized.revision_number)
          ))
          if (!already) {
            this.revisionHistory = [...this.revisionHistory, normalized]
          }
          if (!this.revisionHistorySource || this.revisionHistorySource === 'missing-route' || this.revisionHistorySource === 'empty') {
            this.revisionHistorySource = 'update-response'
          }
          this.revisionHistoryLoaded = true
          return true
        },
        getCandidateListModal() {
          candidateService.getCandidateJoinedListReadyForInvoice(this.invoiceClient, this.invoiceCategory).then( res => {
            if(res.status === "OK") {
              console.log(res)
              this.candidateList = res.data
              this.candidateListError = ''
              this.showModal = true
            } else if(res.status === 500) {
              this.candidateList = []
              this.candidateListError = 'No Candidate with status Joined found for this Client!'
            } else if ((res.status === 401 ) || (res.status === 403 )) {
              window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
              this.candidateListError = 'Session Timed Out! Please login again!!!'
              localStorage.clear();
              this.$router.push({ name: "auth-login" });
            } else {
              this.candidateListError = res
            }
          })
            
        },
        onRowSelected(items) {
          const rowsSelected = items.map((items) => (items))
          console.log(rowsSelected)
          this.selected = []
          for(var i in rowsSelected)
            this.selected.push(rowsSelected[i]);
            
            //this.selected = [...new Set(this.selected)];
          console.log(this.selected)
        },
        clearSelected() {
          this.$refs.selectableTable.clearSelected()
          console.log(this.selected)
        },
        openFile(event) {
          console.log(event.target.files)
          if(event.target.files) {
            this.file = event.target.files[0];
            console.log("file", this.file);
            console.log("cfile", this.invoiceFile)
          } else {
            this.invalidFile = true
            console.log(this.invalidFile)
          }
        },
        getCandidateSelectedList() {
          console.log(this.selected)
          if(this.selected.length > 0) {
            for(var i = 0; i < this.selected.length; i++) {
              const data = {
                cid: this.selected[i].cid,
                jid: this.selected[i].jid,
                candidate_name: this.selected[i].candidate_id + ' - ' + this.selected[i].candidate_name,
                position_name: this.selected[i].position_name,
                // base_amount: 0
                base_amount: this.selected[i].revenue
              }
              this.invoiceCandidateList.push(data)
            }
            this.showInvoiceCandidateList = true
            this.noInvoiceCandidateListError = false
            this.noInvoiceCandidateListError = ''
          } else {
            this.noInvoiceCandidateList = true
            this.noInvoiceCandidateListError = 'No Candidates Selected!!!'
          }
        },
        parseAmount(val) {
          if (val === null || val === undefined || val === '') return 0
          const str = String(val).replace(/,/g, '')
          return parseFloat(str) || 0
        },
        formatAmountDisplay(val) {
          return `${this.parseAmount(val).toFixed(2)}/-`
        },
        getTdsAmount(base) {
          if (!this.tdsApplicable) return 0
          const b = base != null ? this.parseAmount(base) : this.parseAmount(this.totalBaseAmount)
          return parseFloat((b * (this.tdsPercentage / 100)).toFixed(2))
        },
        lineItemDisplayName(item) {
          return item.title || item.itemName || item.description || item.candidateName || item.positionName || `Item ${item.srNo || ''}`.trim()
        },
        createLineItemTitleOption(search) {
          return String(search || '').trim()
        },
        onLineItemTitleChange(index, val) {
          const row = this.lineItems[index]
          if (!row) return
          const title = val != null && String(val).trim() !== '' ? String(val).trim() : ''
          this.$set(row, 'itemName', title)
          this.$set(row, 'title', title)
          this.onLineItemFieldChange(index)
        },
        onBaseAmountInput() {
          this.calculateTotalBaseAmount()
        },
        onCandidateAmountChange() {
          this.calculateTotalBaseAmount()
        },
        onLineItemAmountChange(index) {
          const row = this.lineItems[index]
          if (row && this.parseAmount(row.quantity) > 0) {
            row.unitPrice = parseFloat((this.parseAmount(row.amount) / this.parseAmount(row.quantity)).toFixed(2))
          }
          this.calculateTotalBaseAmount()
        },
        calculateTotalBaseAmount() {
            if (this.invoiceCategory === 'Payroll') {
              this.totalBaseAmount = (this.parseAmount(this.invoiceBaseAmount) * 0.055).toFixed(2)
              this.totalGST = (this.parseAmount(this.totalBaseAmount) * 0.18).toFixed(2)
              this.totalAmountWithGST = (this.parseAmount(this.totalBaseAmount) + this.parseAmount(this.totalGST)).toFixed(2)
              const tdsAmt = this.getTdsAmount(this.totalBaseAmount)
              this.totalAmountWithGSTTDS = (this.parseAmount(this.totalBaseAmount) - tdsAmt + this.parseAmount(this.totalGST)).toFixed(2)
              return
            }

            let base = 0
            if (this.showAmountsLineItemsEditor) {
              base = this.lineItems.reduce((acc, row) => acc + this.parseAmount(row.amount != null ? row.amount : row.baseAmount), 0)
              if (base > 0) {
                this.invoiceBaseAmount = Number(base).toFixed(2)
              }
            } else if (this.showInvoiceCandidateList && this.invoiceCandidateList.length) {
              base = this.invoiceCandidateList.reduce((acc, curr) => acc + this.parseAmount(curr.base_amount), 0)
            } else if (this.invoiceBaseAmount !== null && this.invoiceBaseAmount !== '') {
              base = this.parseAmount(this.invoiceBaseAmount)
            } else {
              base = this.parseAmount(this.totalBaseAmount)
            }

            this.totalBaseAmount = Number(base).toFixed(2)

            if (this.invoiceGST === 'SEZ') {
              this.totalGST = '0.00'
            } else {
              const rate = this.parseAmount(this.gstRatePercent) / 100
              this.totalGST = (this.parseAmount(this.totalBaseAmount) * rate).toFixed(2)
            }

            this.totalAmountWithGST = (this.parseAmount(this.totalBaseAmount) + this.parseAmount(this.totalGST)).toFixed(2)
            const tdsAmt = this.getTdsAmount()
            this.totalAmountWithGSTTDS = (this.parseAmount(this.totalBaseAmount) - tdsAmt + this.parseAmount(this.totalGST)).toFixed(2)

            if (!this.lineItemsFromApi && this.invoiceCandidateList.length) {
              this.lineItems = this.parseLineItemsFromRecord({}, this.invoiceCandidateList)
            }
        },
        validateGST() {
          this.validateGSTError = ''
          if (this.invoiceClientGST.length !== 15) {
            this.validateGSTError = 'GST number should be of 15 characters!!!'
            return false;
          }
          
          // Check if the first two characters are alphabets
          //const firstTwoChars = this.invoiceClientGST.substring(0, 2);
          if (!/\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/.test(this.invoiceClientGST)) {
            this.validateGSTError = 'Not a valid GST number!!!'
            return false;
          }
          return true
        },
        validation() {
          this.clearValidationErrors()
          this.submitError = ''

          if (!this.hasSelectedChangeTypes) {
            return false
          }

          if (this.showBasicSection) {
            this.error.invoiceId = !this.invoiceId || !this.invoiceId.includes('SH') ? 'Invoice Id is not valid' : ''
            this.error.invoiceDate = !this.invoiceDate ? 'Invoice Date is mandatory' : ''
            this.error.invoiceLocation = !this.invoiceLocation ? 'Invoice Location is mandatory' : ''
            this.error.invoiceCategory = !this.invoiceCategory ? 'Invoice Category is mandatory' : ''
            if (this.invoiceCategory && this.invoiceCategory !== 'Recruitment' && this.invoiceCategory !== 'Contractual') {
              this.error.invoiceDescription = !this.invoiceDescription ? 'Invoice Description is mandatory' : ''
            }
          }

          if (this.showClientSection) {
            this.error.invoiceGST = !this.invoiceGST ? 'Invoice GST Type is mandatory' : ''
            this.error.invoiceClient = !this.invoiceClient || this.invoiceClient === 'null' ? 'Invoice Client is mandatory' : ''
            this.error.invoiceClientGST = !this.invoiceClientGST ? 'Invoice Client GST is mandatory' : ''
            if (this.invoiceClientGST) {
              this.validateGST()
            }
          }

          if (this.showStatusSection) {
            this.error.invoiceStatus = !this.invoiceStatus ? 'Invoice Status is mandatory' : ''
            if (this.showR1Fields) {
              this.error.r1Filed = !this.r1Filed ? 'R1 Filed status is required' : ''
            }
          }

          if (this.showPaymentSection && this.isPaidOrPartPaidStatus) {
            this.error.invoicePaidDate = !this.invoicePaidDate ? 'Invoice Paid Date is mandatory for Paid/Part Paid Invoice' : ''
            const paidAmtMissing = this.invoicePaidAmount === null || this.invoicePaidAmount === undefined || this.invoicePaidAmount === ''
            this.error.invoicePaidAmount = paidAmtMissing ? 'Paid Amount is mandatory for Paid/Part Paid Invoice' : ''
          }

          if (this.showEmailDatesSection) {
            this.error.invoiceEmailDate = !this.invoiceEmailDate ? 'Invoice Email Date is mandatory' : ''
            this.error.invoiceDueDate = !this.invoiceDueDate ? 'Invoice Due Date is mandatory' : ''
          }

          if (this.showDocumentSection) {
            this.error.invoiceFile = !this.hasInvoiceFile
              ? 'Invoice File is mandatory'
              : (this.invoiceFile instanceof File && getInvoiceFileSizeError(this.invoiceFile))
                ? INVOICE_FILE_TOO_LARGE_MESSAGE
                : ''
          }

          if (this.showAmountsSection && this.invoiceStatus !== 'Cancelled') {
            const hasBase = this.parseAmount(this.totalBaseAmount) > 0
            if (this.showAmountsLineItemsEditor && !hasBase) {
              this.error.invoiceBaseAmount = 'At least one line item must have an amount'
            } else if (this.showBaseAmountInput && !hasBase) {
              this.error.invoiceBaseAmount = 'Invoice Amount is mandatory'
            } else if (this.isCandidateCategory && this.showInvoiceCandidateList && !hasBase) {
              this.error.invoiceBaseAmount = 'At least one candidate must have a base amount'
            } else {
              this.error.invoiceBaseAmount = ''
            }
          }

          if (this.showRevisionSection && this.invoiceStatus === 'Revised' && !this.showStatusSection && !this.r1Filed) {
            this.error.r1Filed = !this.r1Filed ? 'R1 Filed status is required' : ''
          }

          const revisedSizeError = this.revisedInvoiceFile instanceof File
            ? getInvoiceFileSizeError(this.revisedInvoiceFile)
            : null
          if (revisedSizeError) {
            this.submitError = revisedSizeError
          }

          if (this.noteDateError) {
            this.submitError = this.noteDateError
          }

          const hasFieldError = Object.values(this.error).some(v => v) || !!this.validateGSTError
          if (hasFieldError || revisedSizeError || this.noteDateError) {
            return false
          }
          return true
        },
        onSubmit() {
          this.submitError = ''
          if (this.validation()) {

            const userData = JSON.parse(localStorage.getItem('userData'));

            const addedBy = `${userData.emp_id}`

            const formData = new FormData()

            formData.append('invoice_id', this.invoiceId)
            formData.append('invoice_date', this.invoiceDate)
            // Canonicalize Part Paid alias before submit
            let submitStatus = this.isPartPaidStatus ? 'Part Paid' : this.invoiceStatus
            // Revision workflow always persists live invoices.status = Revised
            const isRevisionSubmit = this.selectedChangeTypes.includes('revision')
              || String(this.$route.query.revise || '') === '1'
              || isRevisedStatusValue(submitStatus)
            if (isRevisionSubmit) {
              submitStatus = REVISED_STATUS
              this.invoiceStatus = REVISED_STATUS
            }
            formData.append('status', submitStatus)
            formData.append('location', this.invoiceLocation)
            formData.append('category', this.invoiceCategory)
            formData.append('vertical', this.invoiceVertical || '')
            formData.append('sattva_gst', this.invoiceSattvaGST ? this.invoiceSattvaGST.toUpperCase() : '')
            formData.append('billing_month', this.invoiceBillingMonth || '')
            formData.append('gst_type', this.invoiceGST)
            formData.append('client', this.invoiceClient)
            formData.append('client_gst', this.invoiceClientGST.toUpperCase())
            if (this.invoiceFile instanceof File) {
              formData.append('invoiceFile', this.invoiceFile)
            }
            formData.append('updated_by', addedBy)
            
            // Add R1 fields if status is not Raised
            if (submitStatus && submitStatus !== 'Raised') {
              formData.append('r1_filed', this.r1Filed)
              formData.append('comment', this.invoiceComment || '')
            }
            
            // Cancellation reason
            if (submitStatus === 'Cancelled') {
              formData.append('cancellation_reason', this.cancellationReason || '')
              formData.append('cancellation_comment', this.cancellationComment || '')
            }

            // GST Hold (Part Paid)
            if (this.isPartPaidStatus) {
              formData.append('is_gst_hold', this.isGSTHold ? '1' : '0')
              formData.append('hold_reason', this.isGSTHold ? (this.gstHoldReason || '') : '')
            }

            // Revision extras — invoice_id / invoice number is NOT regenerated.
            // Backend MUST INSERT into invoice_revisions on each Revised update
            // (N saves → N rows). r1_filed + comment sent above for non-Raised statuses.
            // File versioning: keep same invoice id; store new file as current;
            // archive previous live file as old (previous_pdf / original_invoice_file_path).
            // See backend-handoff/INVOICE_REVISION_API_DOCUMENTATION.md §3.1 for FormData → live Postgres columns.
            if (submitStatus === REVISED_STATUS) {
              formData.append('is_revision', '1')
              formData.append('keep_invoice_id', this.invoiceId || '')
              formData.append('archive_previous_file', '1')
              formData.append('revision_type', this.revisionType || '')
              formData.append('amount_change_type', this.amountChangeType || '')
              formData.append('amend_invoice', this.amendInvoice || '')
              formData.append('revision_reason', this.revisionReason || '')
              formData.append('note_date', this.noteDate || '')
              formData.append('note_sent_to_client', this.noteSentToClient ? '1' : '0')
              formData.append('note_einvoice_done', this.noteEInvoiceDone ? '1' : '0')
              if (this.revisedInvoiceFile instanceof File) formData.append('revisedInvoiceFile', this.revisedInvoiceFile)
              if (this.r1UploadFile instanceof File) formData.append('r1UploadFile', this.r1UploadFile)
              if (this.noteFile instanceof File) formData.append('noteFile', this.noteFile)
            }

            // Only append these fields if status is not Cancelled
            if (submitStatus !== 'Cancelled') {
              if (this.isPaidOrPartPaidStatus) {
                formData.append('paid_date', this.invoicePaidDate || '')
                let paidAmount = this.parseAmount(this.invoicePaidAmount)
                // Paid → full invoice total when amount missing/zero
                if (this.isPaidStatus) {
                  const total = this.parseAmount(this.totalAmountWithGSTTDS || this.totalAmountWithGST)
                  if ((!paidAmount || paidAmount <= 0) && total > 0) paidAmount = total
                }
                formData.append('paid_amount', paidAmount)
              } else if (this.invoicePaidDate) {
                formData.append('paid_date', this.invoicePaidDate)
              }
              if (submitStatus === 'Unpaid' && this.invoicePaymentComment) {
                formData.append('payment_comment', this.invoicePaymentComment)
              }
              formData.append('base_amount', this.parseAmount(this.totalBaseAmount))
              formData.append('due_date', this.invoiceDueDate)
              formData.append('email_date', this.invoiceEmailDate)
              formData.append('description', this.invoiceDescription || '')
              formData.append('po_number', this.invoicePONumber || '')
              formData.append('gst', this.parseAmount(this.totalGST))
              formData.append('gst_amount', this.parseAmount(this.totalGST))
              formData.append('amount_with_gst', this.parseAmount(this.totalAmountWithGST))
              formData.append('total_amount', this.parseAmount(this.totalAmountWithGSTTDS))
              formData.append('gst_rate', this.parseAmount(this.gstRatePercent))
              formData.append('tds_applicable', this.tdsApplicable ? '1' : '0')
              if (this.tdsApplicable) {
                formData.append('tds_amount', this.getTdsAmount())
                formData.append('tds_percentage', this.tdsPercentage)
              }
              if (this.showAmountsSection && this.lineItems.length) {
                this.appendLineItemsToFormData(formData)
              }
              const candidatesForSubmit = this.invoiceCandidateList.map(c => ({
                ...c,
                base_amount: this.parseAmount(c.base_amount)
              }))
              formData.append('candidates', JSON.stringify(candidatesForSubmit))
            }
            // Use updateInvoice (/invoice/update/:id) with invoiceId — invoice_id was undefined here.
            invoiceService.updateInvoice(this.invoiceId, formData).then(async res => {
                if (!res) {
                  this.submitError = 'Invoice update failed (no response from API).'
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: 'Invoice update failed',
                      text: this.submitError,
                      icon: 'AlertCircleIcon',
                      variant: 'danger',
                      timeout: 7000,
                    },
                  })
                  return
                }
                if (res.status === "OK") {
                    console.log(res)
                    if (submitStatus === REVISED_STATUS || isRevisionSubmit) {
                      const statusCheck = await this.ensureRevisedStatusPersisted(res)
                      if (!statusCheck.ok) {
                        this.submitError = statusCheck.message
                        this.$toast({
                          component: ToastificationContent,
                          props: {
                            title: 'Status not set to Revised',
                            text: statusCheck.message,
                            icon: 'AlertCircleIcon',
                            variant: 'danger',
                            timeout: 10000,
                          },
                        })
                        return
                      }

                      await this.loadRevisionHistory()
                      // If history GET 404/empty but PUT returned a revision object, use it.
                      if (!this.revisionHistory.length) {
                        this.mergeRevisionFromUpdateResponse(res)
                      }
                      await this.loadInvoiceTimeline()

                      const histEmpty = !this.revisionHistory.length
                      const missingRoute = this.revisionHistorySource === 'missing-route'
                      if (histEmpty || missingRoute) {
                        // Honest gap: client only PUTs FormData; it never invents revision rows.
                        const gapText = missingRoute
                          ? 'Status is Revised on the invoice, but GET /invoice/:id/revision/history returned 404. Timeline “Revised” events and revision logs stay empty until the backend INSERTs into invoice_revisions on Revised PUT and exposes that route.'
                          : 'Status is Revised, but invoice_revisions has no row for this invoice (history GET empty). Backend must INSERT into invoice_revisions when PUT /invoice/update/:id receives status=Revised (or is_revision=1). Client cannot write Postgres.'
                        this.$toast({
                          component: ToastificationContent,
                          props: {
                            title: 'Revised — revision log missing',
                            text: gapText,
                            icon: 'AlertTriangleIcon',
                            variant: 'warning',
                            timeout: 12000,
                          },
                        })
                      } else {
                        this.$toast({
                          component: ToastificationContent,
                          props: {
                            title: 'Invoice revised successfully',
                            text: 'Status set to Revised. Compare original and revised versions in Invoice versions.',
                            icon: 'EditIcon',
                            variant: 'primary',
                            timeout: 5000,
                          },
                        })
                      }
                    } else {
                      this.$toast({
                        component: ToastificationContent,
                        props: {
                          title: 'Invoice Updated Successfully !!!',
                          icon: 'EditIcon',
                          variant: 'primary',
                          timeout: 5000,
                        },
                      })
                    }
                    this.$router.push({ name: "apps-invoice-dashboard" });
                } else if ((res.status === 401 ) || (res.status === 403 )) {
                  window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
                  localStorage.clear();
                  this.$router.push({ name: "auth-login" });
                } else if ((res.status === 402 ) ) {
                  this.submitError = formatInvoiceApiError(res, 'Failed! Some Error Occurred.. Please Contact Admin!!!')
                } else if ((res.status === 400 ) ) {
                  this.submitError = formatInvoiceApiError(res, 'Failed! Some Error Occurred.. Please Contact Admin!!!')
                  this.$toast({
                      component: ToastificationContent,
                      props: {
                        title: 'Invoice update failed',
                        text: this.submitError,
                        icon: 'AlertCircleIcon',
                        variant: 'danger',
                        timeout: 7000,
                      },
                    })
                }
                else if ((res.status === 500 ) || (res.status === 'ERROR')) {
                  this.submitError = formatInvoiceApiError(res, 'Invoice update failed')
                  const raw = res.data != null ? res.data : res
                  if (raw && JSON.stringify(raw).includes('Validation error')) {
                    try {
                      const msg = raw.error && raw.error.errors && raw.error.errors[0]
                        ? raw.error.errors[0].message
                        : ''
                      if (msg === 'invoice_id must be unique') {
                        this.submitError = `${this.submitError} Invoice ID already Exists!!!`
                      }
                    } catch (_) { /* ignore */ }
                  }
                  this.$toast({
                      component: ToastificationContent,
                      props: {
                        title: 'Invoice update failed',
                        text: this.submitError,
                        icon: 'AlertCircleIcon',
                        variant: 'danger',
                        timeout: 7000,
                      },
                    })
                } else {
                  this.submitError = formatInvoiceApiError(res, 'Invoice update failed')
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: 'Invoice update failed',
                      text: this.submitError,
                      icon: 'AlertCircleIcon',
                      variant: 'danger',
                      timeout: 7000,
                    },
                  })
                }
            })
            //console.log('invoice', invoice)
          } else {
            console.log('Validation Error')
            this.submitError = this.submitError || 'ERROR! - Mandatory Field Missing Or Invalid Data!!!'
          }
           
        },
        
        previewFile() {
          if (this.invoiceFile instanceof File) {
            if (this.blink && this.blink.startsWith('blob:')) URL.revokeObjectURL(this.blink)
            this.blink = URL.createObjectURL(this.invoiceFile)
          }
          this.$refs["modal-xl-invoice-preview"].show()
        },
        getFile() {
          const filePath = this.existingInvoiceFilePath || (this.invoiceFile instanceof File ? null : null)
          if (!filePath || typeof filePath !== 'string') return
          const key = filePath.replace("/","%2F")
          JobService.getFileBase64(key)
              .then(res => {
                console.log(res)
                const base64 = res.file;
                this.blink = "data:application/pdf;base64," + base64
                
            }); 
        },

        appendLineItemsToFormData(formData) {
          this.lineItems.forEach((row, index) => {
            formData.append(`lineItems[${index}][itemName]`, row.itemName || row.title || '')
            formData.append(`lineItems[${index}][title]`, row.title || row.itemName || '')
            formData.append(`lineItems[${index}][amount]`, this.parseAmount(row.amount))
            formData.append(`lineItems[${index}][quantity]`, this.parseAmount(row.quantity) || 1)
            formData.append(`lineItems[${index}][unitPrice]`, this.parseAmount(row.unitPrice))
            if (row.sacCode) formData.append(`lineItems[${index}][sacCode]`, row.sacCode)
          })
          try {
            formData.append('line_items_json', JSON.stringify(this.lineItems))
          } catch (_) { /* ignore stringify errors */ }
        },
        async approveInvoice() {
          try {
            const formData = new FormData()
            formData.append('status', 'Issued')
            formData.append('approved_by', JSON.parse(localStorage.getItem('userData') || '{}').emp_id || '')
            const res = await invoiceService.updateInvoice(this.$route.params.id, formData)
            if (res && res.status === 'OK') {
              this.invoiceStatus = 'Issued'
              this.$bvToast.toast('Invoice approved and issued.', { title: 'Approved', variant: 'success', solid: true })
            } else {
              this.$bvToast.toast(res?.message || 'Failed to approve.', { title: 'Error', variant: 'danger', solid: true })
            }
          } catch (e) {
            this.$bvToast.toast('Failed to approve invoice.', { title: 'Error', variant: 'danger', solid: true })
          }
        },

        async returnInvoice() {
          const reason = prompt('Enter reason for returning to maker:')
          if (!reason) return
          try {
            const formData = new FormData()
            formData.append('status', 'Returned')
            formData.append('return_reason', reason)
            const res = await invoiceService.updateInvoice(this.$route.params.id, formData)
            if (res && res.status === 'OK') {
              this.invoiceStatus = 'Returned'
              this.$bvToast.toast('Invoice returned to maker.', { title: 'Returned', variant: 'warning', solid: true })
            } else {
              this.$bvToast.toast(res?.message || 'Failed to return.', { title: 'Error', variant: 'danger', solid: true })
            }
          } catch (e) {
            this.$bvToast.toast('Failed to return invoice.', { title: 'Error', variant: 'danger', solid: true })
          }
        }

    },
}
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.update-invoice-page {
  // Navbar already shows meta.pageTitle — reclaim content-wrapper top gap
  // (same as pd-reclaim-navbar-gap in payroll-docs-chrome.scss)
  margin-top: -0.85rem;

  .live-preview-sticky {
    position: sticky;
    top: 0.5rem;
  }
  .preview-toolbar-row {
    margin-bottom: 0.25rem;
    min-height: 28px;
  }
  .preview-live-badge {
    padding: 0.15rem 0.4rem;
    font-size: 0.7rem;
    line-height: 1.1;
    font-weight: 500;
  }
  .live-preview-scroll {
    max-height: calc(100vh - 7rem);
    overflow-y: auto;
    padding-right: 0.25rem;
  }
  .a4-preview-stage {
    width: 100%;
    padding: 0.65rem 0.35rem 1rem;
    background: linear-gradient(180deg, #ececef 0%, #e4e4e8 100%);
    border-radius: 0.35rem;
    overflow: hidden;
  }
  .a4-preview-scaler {
    will-change: transform;
  }

  .update-change-type-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .update-change-type-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #334155;
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.35;
    min-height: 1.75rem;
    padding: 0.25rem 0.55rem;
    border-radius: 999px;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.12s ease;
    cursor: pointer;

    &:hover {
      border-color: rgba(0, 153, 172, 0.4);
      color: #008B7D;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    &.is-active {
      background: rgba(0, 153, 172, 0.12);
      border-color: rgba(0, 153, 172, 0.45);
      color: #008B7D;
    }

    .chip-icon {
      flex-shrink: 0;
    }
  }

  .update-section-summary {
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .update-section-done-badge {
    font-size: 0.6rem;
    padding: 0.15rem 0.35rem;
    line-height: 1.1;
  }

  .update-section-active {
    border: 1px solid rgba(0, 153, 172, 0.2);
    border-left: 3px solid #0099AC;
    border-radius: 0.3rem;
    padding: 0.3rem 0.45rem;
    margin-top: 0.25rem;
    background: #fff;

    .form-group {
      margin-bottom: 0.25rem;
    }
  }

  .update-compact-alert {
    display: flex;
    align-items: flex-start;
    padding: 0.2rem 0.45rem !important;
    margin-bottom: 0.25rem;
    font-size: 0.8125rem;
    line-height: 1.35;
  }

  .update-revision-panel {
    border: 1px solid rgba(255, 159, 67, 0.45);
    border-radius: 0.25rem;
    padding: 0.35rem 0.45rem;
    background: rgba(255, 159, 67, 0.04);
  }

  .update-radio-field {
    margin-bottom: 0.25rem;

    > label {
      color: #5e5873;
      font-weight: 400;
      font-size: 0.8125rem;
    }
  }

  .update-radio-group-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;

    ::v-deep .btn-group {
      display: flex;
      flex-wrap: wrap;
      gap: 0.35rem;
    }

    ::v-deep .btn-group > .btn {
      position: relative;
      flex: 0 1 auto;
      margin: 0 !important;
      border-radius: 0.25rem !important;
      font-size: 0.8125rem;
      font-weight: 400;
      line-height: 1.35;
      padding: 0.2rem 0.55rem;
      white-space: nowrap;
      text-align: center;
    }
  }

  .update-section-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    margin-top: 0.5rem;
    padding-top: 0.4rem;
    border-top: 1px solid #ebe9f1;
  }

  .update-section-fade-enter-active,
  .update-section-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .update-section-fade-enter,
  .update-section-fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
  }

  .update-section-list-enter-active,
  .update-section-list-leave-active {
    transition: all 0.25s ease;
  }

  .update-section-list-enter,
  .update-section-list-leave-to {
    opacity: 0;
    transform: translateX(-8px);
  }

  .update-invoice-context {
    line-height: 1.3;
  }

  .update-invoice-context-row {
    gap: 0.35rem;
  }

  /* ── Unified update form typography & density ── */
  .update-invoice-form {
    --uif-font-size: 0.8125rem;
    --uif-font-size-btn: 0.875rem;
    --uif-control-height: 31px;
    --uif-font-weight-label: 400;
    --uif-font-weight-value: 500;

    font-family: inherit;
    font-size: var(--uif-font-size);
    font-weight: var(--uif-font-weight-label);
    line-height: 1.35;

    .update-field-label,
    .form-group > label,
    .form-group legend,
    .col-form-label {
      font-family: inherit;
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-label);
      margin-bottom: 0.15rem;
      color: #5e5873;
    }

    .update-section-subheading,
    .update-session-label {
      font-family: inherit;
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-value);
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: #6e6b7b;
    }

    .update-session-label {
      letter-spacing: 0.04em;
      margin-bottom: 0.25rem;
    }

    .wizard-question-heading {
      font-size: var(--uif-font-size-btn);
      font-weight: var(--uif-font-weight-value);
    }

    .wizard-question-hint,
    .form-group small,
    .text-muted,
    .update-total-hint {
      font-family: inherit;
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-label);
      line-height: 1.35;
    }

    .form-group {
      margin-bottom: 0.25rem;
    }

    .form-control,
    .custom-select,
    select.custom-select-sm {
      font-family: inherit !important;
      font-size: var(--uif-font-size) !important;
      font-weight: var(--uif-font-weight-label) !important;
      height: var(--uif-control-height) !important;
      min-height: var(--uif-control-height) !important;
      padding-top: 0.2rem !important;
      padding-bottom: 0.2rem !important;
      line-height: 1.35 !important;
    }

    textarea.form-control {
      height: auto !important;
      min-height: 0 !important;
      font-size: var(--uif-font-size) !important;
    }

    .v-select {
      font-family: inherit;
      font-size: var(--uif-font-size);

      .vs__dropdown-toggle {
        min-height: var(--uif-control-height) !important;
        padding: 0 4px !important;
      }

      .vs__selected {
        margin: 1px 4px 1px 0;
        line-height: 1.35;
        font-size: var(--uif-font-size);
        font-weight: var(--uif-font-weight-label);
      }

      .vs__search,
      .vs__selected,
      .vs__actions {
        padding: 0 2px;
      }

      .vs__search,
      .vs__selected,
      ::placeholder {
        font-size: var(--uif-font-size);
        font-weight: var(--uif-font-weight-label);
      }

      .vs__clear,
      .vs__open-indicator {
        transform: scale(0.85);
      }
    }

    .update-line-items-table {
      font-family: inherit;
      font-size: var(--uif-font-size);

      th,
      td {
        padding: 0.2rem 0.35rem;
        vertical-align: middle;
        font-size: var(--uif-font-size);
        font-weight: var(--uif-font-weight-label);
      }

      .form-control {
        height: 28px !important;
        min-height: 28px !important;
      }

      .update-line-item-title-select .vs__dropdown-toggle {
        min-height: 28px !important;
        padding: 0 2px !important;
      }

      .update-line-item-title-select .vs__search,
      .update-line-item-title-select .vs__selected {
        font-size: 0.8rem;
        margin: 0;
      }
    }

    .update-tds-inline {
      min-height: var(--uif-control-height);
      gap: 0.35rem;
    }

    .update-tds-switch {
      .custom-control-label {
        white-space: nowrap;
        padding-top: 0.1rem;
      }
    }

    .custom-switch {
      min-height: var(--uif-control-height);
      padding-top: 0.15rem;
    }

    .custom-control-label {
      font-family: inherit;
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-label);
      line-height: 1.35;
    }

    .update-total-item {
      margin-bottom: 0.25rem;
    }

    .update-total-label {
      display: block;
      font-family: inherit;
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-label);
      color: #6e6b7b;
      line-height: 1.35;
    }

    .update-total-value {
      display: block;
      font-family: inherit;
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-value);
      color: #5e5873;
      line-height: 1.35;
    }

    .update-total-hint {
      display: block;
      color: #6e6b7b;
      margin-top: 0.1rem;
    }

    .amounts-totals-row {
      padding-top: 0.15rem;
    }

    .badge {
      font-family: inherit;
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-value);
    }

    .update-section-done-badge {
      font-size: 0.6875rem;
    }

    .wizard-summary-lines,
    .wizard-summary-card strong {
      font-family: inherit;
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-value);
    }

    .update-compact-alert {
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-label);
    }

    .update-change-type-chip {
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-value);
      min-height: 1.75rem;
      padding: 0.25rem 0.55rem;
    }

    .btn,
    .wizard-nav-btn.btn {
      font-family: inherit;
      font-size: var(--uif-font-size-btn);
      font-weight: var(--uif-font-weight-value);
    }

    .btn-group-toggle .btn.btn-sm,
    .custom-radio .custom-control-label {
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-label);
    }

    .custom-file-label,
    .custom-file-label-sm {
      font-family: inherit;
      font-size: var(--uif-font-size);
      font-weight: var(--uif-font-weight-label);
      min-height: var(--uif-control-height);
      height: var(--uif-control-height);
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;
    }
  }
}
</style>
<style src="./components/wizard/wizard-step-shared.css"></style>